import React, { Component } from "react";
import { Col, Form, Row, Button, Accordion, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./motorpolicies.css";
import DatePicker from "react-datepicker";
import Select from "react-select";
import configData from "../../config.json";

// Enhanced policy plan mapping with Excel requirements
const POLICY_PLAN_MAPPING = {
  "Package (3 years OD + 3 years TP)": { id: 6, tenure: "3" },
  "Package (1 year OD + 1 year TP)": { id: 2, tenure: "1" },
  "Package (2 years OD + 2 years TP)": { id: 4, tenure: "2" },
  "Package (1 year OD + 5 years TP)": { id: 7, tenure: "1" },
  "Package (5 years OD + 5 years TP)": { id: 8, tenure: "5" },
  "Standalone TP (1 year)": { id: 1, tenure: "1" },
  "Standalone TP (5 years)": { id: 3, tenure: "5" },
  "Standalone OD (1 year)": { id: 5, tenure: "1" },
  "Standalone TP (2 years)": { id: 9, tenure: "2" },
  "Standalone TP (3 years)": { id: 10, tenure: "3" }
};

// Enhanced vehicle age limits based on Excel requirements
// SAOD: 4 years 08 months 28 days = 4.74 years (4 + 8/12 + 28/365)
// RTI: 3 years 08 months 28 days = 3.74 years (for RTI add-on eligibility)
const ENHANCED_VEHICLE_AGE_LIMITS = {
  "Package (3 years OD + 3 years TP)": { maxAge: 15.69, businessTypes: ["Roll Over", "New Business", "Used Vehicle"] },
  "Package (1 year OD + 1 year TP)": { maxAge: 15.69, businessTypes: ["Roll Over", "New Business", "Used Vehicle"] },
  "Package (2 years OD + 2 years TP)": { maxAge: 15.69, businessTypes: ["Roll Over", "New Business", "Used Vehicle"] },
  "Package (1 year OD + 5 years TP)": { maxAge: 15.69, businessTypes: ["New Business"] },
  "Package (5 years OD + 5 years TP)": { maxAge: 5, businessTypes: ["New Business"] },
  "Standalone TP (1 year)": { maxAge: 20.69, businessTypes: ["Roll Over", "New Business", "Breaking"] },
  "Standalone TP (5 years)": { maxAge: 20.69, businessTypes: ["New Business", "Roll Over", "Breaking"] },
  "Standalone OD (1 year)": { maxAge: 4.74, businessTypes: ["New Business", "Roll Over", "Used Vehicle"] }, // ✅ SAOD: 4 years 08 months 28 days
  "Standalone TP (2 years)": { maxAge: 20.69, businessTypes: ["Roll Over", "New Business", "Breaking"] },
  "Standalone TP (3 years)": { maxAge: 20.69, businessTypes: ["Roll Over", "New Business", "Breaking"] }
};

// RTI (Return to Invoice) age limit: 3 years (vehicle age ≤ 3 years)
const RTI_MAX_AGE_YEARS = 3.74; // 3 years for RTI eligibility

// 5-year condition add-ons age limit
const FIVE_YEAR_ADDON_MAX_AGE = 5.0; // For Zero Dep, Consumable, RSA, EME

// ============================================
// SAOD (Standalone Own Damage) Allowed Add-ons
// Vehicle Age Limit: ≤ 4 Years 8 Months 28 Days
// ============================================
const SAOD_ALLOWED_ADDONS = [
  // ✅ Add-On Covers (with conditions)
  'dep_reimburse', 'dep_reimb',            // Depreciation Reimbursement - 5 year condition
  'return_invoice', 'rtn_invoice',          // Return to Invoice (RTI) - 3 year condition
  'consumbale_expense', 'consumable',       // Consumable Expenses - 5 year condition
  'add_towing', 'add_towing_amount',        // Additional Towing Charges - No specific condition
  'rsa',                                    // Road Side Assistance - 5 year condition
  'emg_med_exp', 'emg_med_exp_si',          // Emergency Medical Expenses - 5 year condition

  // ✅ ALL Discount Covers - YES for SAOD
  'antitheft_cover',                        // Anti-theft Discount
  'vehicle_blind',                          // Blind / Handicap Discount
  'own_premises',                           // Limited to Own Premises
  'driving_tuition',                        // Driving Tuition
  'uw_discount',                            // Underwriting Discount
  'uw_loading',                             // Underwriting Loading
  'voluntary_deductibles', 'voluntary_amount', // Voluntary Deductibles
  'tppd_discount',                          // TPPD Discount

  // ✅ Accessories (always needed for OD)
  'electrical_acc', 'electrical_si',
  'non_electrical_acc', 'non_electrical_si',
  'bi_fuel_kit', 'bi_fuel_kit_si'

  // ❌ NOT Allowed in SAOD:
  // - TPPD Restricted (add_tppd) - NO
  // - All PA Covers (pa_owner, pa_unnamed, pa_paid) - NO
  // - Legal Liability Covers (ll_paid, ll_emp) - NO
];

// ============================================
// SATP (Standalone Third Party) Allowed Add-ons
// Vehicle Age Limit: ≤ 20 Years 8 Months 23 Days
// ============================================
const SATP_ALLOWED_ADDONS = [
  // ✅ Optional Add-ons for SATP
  'add_tppd', 'add_tppd_si',                              // TPPD Restricted - YES/NO (Optional)
  'pa_owner', 'pa_owner_tenure', 'pa_owner_declaration',  // CPA - YES/NO (Optional)
  'pa_unnamed', 'pa_unnamed_no', 'pa_unnamed_si',         // PA Unnamed - YES/NO (Optional)
  'pa_paid', 'pa_paid_no', 'pa_paid_si'                   // PA Paid Driver - YES/NO (Optional)

  // ❌ NOT Allowed in SATP:
  // - Depreciation Reimbursement - NO
  // - Return to Invoice - NO
  // - Consumable Expenses - NO
  // - Additional Towing Charges - NO
  // - Road Side Assistance - NO
  // - Emergency Medical Expenses - NO
  // - All Discount Covers - NO
  // - Legal Liability Covers (ll_paid, ll_emp) - NO
];

// ============================================
// Package Policy Allowed Add-ons
// ============================================
const PACKAGE_ALLOWED_ADDONS = [
  // ✅ Add-On Covers (with conditions)
  'dep_reimburse', 'dep_reimb',            // Depreciation Reimbursement - 5 year condition
  'return_invoice', 'rtn_invoice',          // Return to Invoice (RTI) - 3 year condition
  'consumbale_expense', 'consumable',       // Consumable Expenses - 5 year condition
  'add_towing', 'add_towing_amount',        // Additional Towing Charges - Available
  'rsa',                                    // Road Side Assistance - 5 year condition
  'emg_med_exp', 'emg_med_exp_si',          // Emergency Medical Expenses - 5 year condition

  // ✅ TPPD
  'add_tppd', 'add_tppd_si',                // TPPD Restricted

  // ✅ PA Covers
  'pa_owner', 'pa_owner_tenure', 'pa_owner_declaration',  // CPA
  'pa_unnamed', 'pa_unnamed_no', 'pa_unnamed_si',         // PA Unnamed
  'pa_paid', 'pa_paid_no', 'pa_paid_si',                  // PA Paid Driver

  // ✅ Discount Covers
  'antitheft_cover',                        // Anti-theft Discount
  'vehicle_blind',                          // Blind / Handicap Discount
  'own_premises',                           // Limited to Own Premises
  'driving_tuition',                        // Driving Tuition
  'uw_discount',                            // Underwriting Discount
  'uw_loading',                             // Underwriting Loading
  'voluntary_deductibles', 'voluntary_amount', // Voluntary Deductibles
  'tppd_discount',                          // TPPD Discount

  // ✅ Accessories
  'electrical_acc', 'electrical_si',
  'non_electrical_acc', 'non_electrical_si',
  'bi_fuel_kit', 'bi_fuel_kit_si'

  // ❌ Legal Liability Restrictions for Package:
  // - Legal Liability to Employees IMT 29 (ll_emp) - NO
  // - Legal Liability to paid drivers/cleaners/conductor IMT 28 (ll_paid) - NO
];

// Add-ons with 5-year vehicle age condition
const FIVE_YEAR_CONDITION_ADDONS = [
  'dep_reimburse', 'dep_reimb',            // Zero Dep - 5 year condition
  'consumbale_expense', 'consumable',       // Consumable - 5 year condition
  'rsa',                                    // RSA - 5 year condition
  'emg_med_exp', 'emg_med_exp_si'           // EME - 5 year condition
];

// Add-ons with 3-year vehicle age condition
const THREE_YEAR_CONDITION_ADDONS = [
  'return_invoice', 'rtn_invoice'           // RTI - 3 year condition
];

// NCB progression mapping from Excel requirements
const NCB_PROGRESSION = {
  "0": "20",
  "20": "25",
  "25": "35",
  "35": "45",
  "45": "50",
  "50": "50"
};



export default class Motorquotes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Session and core data
      activeKey: "0",
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      integId: 327,

      // API data
      apiData: {},
      motoraddons: [],
      motorproducts: [],
      filtered_addons: [],
      addon_values: {},
      hasPreviousPolicy: false,
      // Vehicle data
      vehicle_make: null,
      vehicle_model: null,
      vehicle_variant: null,
      models: [],
      variants: [],
      make_code: "",
      model_code: "",
      variant_code: "",

      // Policy data
      plan_type: "",
      business_type: "",
      proposer_type: "",
      pol_start_date: "",
      pol_end_date: "",
      tp_end_date: "",
      prev_pol_start_date: "",
      prev_pol_end_date: "",
      prev_policy_type: "",
      pre_pol_ncb: "0",

      // Registration data
      regno_1: "",
      regno_2: "",
      regno_3: "",
      regno_4: "",
      place_reg: "",
      place_reg_no: "",
      proposer_pincode: "",
      dor: "",
      man_year: "",
      manu_month: "",

      // Vehicle age related
      vehicle_age: 0,
      vehicle_age_display: "",
      precise_vehicle_age: { years: 0, months: 0, days: 0, totalInYears: 0 },

      // SATP specific
      is_satp: true,
      cng_kit_selected: false,
      cng_annual_charge: 60,
      future_expiry_date: "",
      disclaimer_accepted: false,

      // Bundle dates (for Roll Over)
      bletpstart: "",
      bletpend: "",
      bleodstart: "",
      bleodend: "",

      // Excel Requirements - Additional fields
      vehicle_idv: "",

      prev_insurer: "",
      prev_policy_had_zero_dep: false,
      electrical_accessories_amount: "",
      non_electrical_accessories_amount: "",
      puc_expiry_date: "",
      puc_not_provided: false,
      financier_name: "",
      financier_branch: "",
      terms_accepted: false,
      breaking_period_days: "",
      current_ncb_calculated: "0",

      // CPA dates for SATP
      cpa_start_date: "",
      cpa_end_date: "",

      // UI state
      showSecondAccordion: false,
      isLoading: false,
      isTPDisabled: false,
    };
  }

  // Utility Methods
  showToast = (msg, type) => {
    const toastMethods = {
      success: toast.success,
      error: toast.error,
      warning: toast.warn,
      info: toast.info
    };
    (toastMethods[type] || toast.success)(msg);
  };

  formatDate = (date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  // Enhanced Vehicle Age Calculation (Excel Requirements)
  // ✅ Calculate precise vehicle age in years, months, days, and total years
  calculatePreciseVehicleAge = (registrationDate) => {
    if (!registrationDate) return { years: 0, months: 0, days: 0, totalInYears: 0 };

    const regDate = new Date(registrationDate);
    const currentDate = new Date();

    let years = currentDate.getFullYear() - regDate.getFullYear();
    let months = currentDate.getMonth() - regDate.getMonth();
    let days = currentDate.getDate() - regDate.getDate();

    // Adjust for negative days
    if (days < 0) {
      months--;
      const lastDayPrevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
      days += lastDayPrevMonth;
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    // Total years (with decimals)
    const totalInYears = years + months / 12 + days / 365;

    return {
      years,
      months,
      days,
      totalInYears: Math.round(totalInYears * 100) / 100
    };
  };


  calculateVehicleAge = (registrationDate) => {
    if (!registrationDate) return 0;
    const preciseAge = this.calculatePreciseVehicleAge(registrationDate);
    return preciseAge.totalInYears;
  };

  formatVehicleAge = (age) => {
    const years = Math.floor(age);
    const months = Math.round((age - years) * 12);
    return `${years}Y ${months}M`;
  };

  formatPreciseVehicleAge = (preciseAge) => {
    return `${preciseAge.years}Y ${preciseAge.months}M ${preciseAge.days}D`;
  };

  // Enhanced Plan Eligibility Based on Excel Requirements
  isVehicleEligibleForPlan = (vehicleAge, businessType, planName) => {
    const ageLimit = ENHANCED_VEHICLE_AGE_LIMITS[planName];

    if (!ageLimit) {
      console.warn(`No age limit defined for plan: ${planName}`);
      return true;
    }

    if (!ageLimit.businessTypes.includes(businessType)) {
      return false;
    }

    return vehicleAge <= ageLimit.maxAge;
  };

  // Enhanced Age Validation with Precise Excel Limits
  isVehicleEligibleForPlanPrecise = (registrationDate, businessType, planName) => {
    if (!registrationDate || !businessType || !planName) return true;

    const preciseAge = this.calculatePreciseVehicleAge(registrationDate);

    // ✅ Rule 2: SAOD Special Rule: Max 4 years 08 months 28 days (4.74 years)
    if (planName.toLowerCase().includes('standalone od') || planName.toLowerCase().includes('saod')) {
      if (preciseAge.totalInYears > 4.74) { // 4 years 08 months 28 days
        console.warn(`❌ SAOD plan not eligible: Vehicle age ${preciseAge.totalInYears.toFixed(2)} years exceeds 4Y 8M 28D limit`);
        return false;
      }
    }

    // Excel Requirements: Package: 15Y 8M 25D, SATP: 20Y 8M 23D, SAOD: 4Y 8M 28D
    const ageLimits = {
      package: { years: 15, months: 8, days: 25 },
      satp: { years: 20, months: 8, days: 23 },
      saod: { years: 4, months: 8, days: 28 } // ✅ Rule 2: 4 years 08 months 28 days
    };

    let applicableLimit;
    if (planName.toLowerCase().includes('package')) {
      applicableLimit = ageLimits.package;
    } else if (planName.toLowerCase().includes('standalone tp')) {
      applicableLimit = ageLimits.satp;
    } else if (planName.toLowerCase().includes('standalone od')) {
      applicableLimit = ageLimits.saod;
    }

    if (!applicableLimit) return true;

    // Check precise age eligibility
    if (preciseAge.years > applicableLimit.years) return false;
    if (preciseAge.years === applicableLimit.years) {
      if (preciseAge.months > applicableLimit.months) return false;
      if (preciseAge.months === applicableLimit.months && preciseAge.days > applicableLimit.days) return false;
    }

    return true;
  };

  // ✅ Returns only the eligible plans based on DOR, Business Type, and Plan Rules
  getEligiblePlans = () => {
    const { motorproducts, dor, business_type } = this.state;

    // Step 1: Basic validations
    if (!business_type || !dor || !motorproducts.length) {
      console.warn("Eligibility check skipped: Missing business_type, DOR, or motorproducts not loaded.");
      return motorproducts;
    }

    // Step 2: Calculate precise vehicle age
    const preciseAge = this.calculatePreciseVehicleAge(dor);
    const ageInYears = preciseAge.totalInYears;

    console.log(`🔍 Checking eligible plans for vehicle age: ${ageInYears} years (${preciseAge.years}Y ${preciseAge.months}M ${preciseAge.days}D)`);

    // Step 3: Filter eligible plans
    const eligiblePlans = motorproducts.filter(product => {
      const planName = (product.productName || product.planName || product.name || "").toLowerCase();

      // ✅ Rule 2: SAOD Rule: Allow only if DOR <= 4 years 08 months 28 days (4.74 years)
      if ((planName.includes("saod") || planName.includes("standalone od")) && ageInYears > 4.74) {
        console.warn(`❌ ${planName.toUpperCase()} not applicable for vehicles older than 4Y 8M 28D (${ageInYears.toFixed(2)} years).`);
        return false;
      }

      // ✅ Standard Excel-based eligibility (package, SATP, etc.)
      const isEligible = this.isVehicleEligibleForPlanPrecise(dor, business_type, planName);

      if (!isEligible) {
        console.warn(`🚫 ${planName.toUpperCase()} plan not eligible based on Excel-defined limits.`);
      }

      return isEligible;
    });

    // Step 4: Log summary
    console.log(`✅ Found ${eligiblePlans.length} eligible plan(s) out of ${motorproducts.length}.`);

    // Step 5: Only show error if no plans available
    if (eligiblePlans.length === 0) {
      this.showToast("⚠️ No eligible plans available for the selected Date of Registration.", "error");
    }
    // Removed success toast - silent operation

    // Step 6: Return eligible plans
    return eligiblePlans;
  };


  updateVehicleAgeInfo = (registrationDate) => {
    const age = this.calculateVehicleAge(registrationDate);
    const preciseAge = this.calculatePreciseVehicleAge(registrationDate);
    const ageDisplay = this.formatVehicleAge(age);
    const preciseAgeDisplay = this.formatPreciseVehicleAge(preciseAge);

    this.setState({
      vehicle_age: age,
      vehicle_age_display: ageDisplay,
      precise_vehicle_age: preciseAge
    });

    // Removed info toast - vehicle age shown in UI
    console.log(`Vehicle age calculated: ${preciseAgeDisplay}`);

    if (this.state.business_type) {
      this.checkPlanEligibility(age);
    }
  };

  checkPlanEligibility = (vehicleAge) => {
    // ✅ Step 1: Basic sanity checks
    const { dor, business_type, motorproducts, precise_vehicle_age } = this.state;

    if (!dor) {
      this.showToast("Please select Date of Registration first", "warning");
      return;
    }

    if (!business_type) {
      this.showToast("Please select Business Type first", "warning");
      return;
    }

    // ✅ Step 2: Wait for motor products to load (fixes random 'No eligible plans' bug)
    if (!motorproducts || motorproducts.length === 0) {
      console.warn("Motor products not yet loaded. Retrying eligibility check...");
      setTimeout(() => this.checkPlanEligibility(vehicleAge), 500);
      return;
    }

    // ✅ Step 3: Calculate eligible plans
    const eligiblePlans = this.getEligiblePlans();
    const preciseDisplay = this.formatPreciseVehicleAge(precise_vehicle_age);

    // ✅ Step 4: Display message & handle state update
    if (eligiblePlans.length === 0) {
      this.showToast(
        `⚠️ No eligible plans available for ${preciseDisplay} old vehicle (${business_type} type).`,
        "error"
      );

      // Reset dependent states
      this.setState({
        plan_type: "",
        filtered_addons: [],
        addon_values: {},
      });
    } else {
      // Removed success toast - silent operation
      console.log(`✅ ${eligiblePlans.length} eligible plan(s) found for ${preciseDisplay} old vehicle.`);

      // Optional: auto-select first eligible plan if user hasn't chosen one
      if (!this.state.plan_type) {
        const firstPlan = eligiblePlans[0].productName || eligiblePlans[0].planName || eligiblePlans[0].name;
        this.setState({ plan_type: firstPlan });
        // Removed info toast - silent auto-selection
        console.log(`Auto-selected first eligible plan: ${firstPlan}`);
      }
    }

    // ✅ Step 5: Store eligible plans in state (useful if you want to render them later)
    this.setState({ eligiblePlans });
  };


  // NCB Auto-increment Handler (Excel Requirement)
  handleNCBAutoIncrement = (prevNCB, businessType) => {
    if (businessType !== "Roll Over") return prevNCB;

    const newNCB = NCB_PROGRESSION[prevNCB.toString()];
    if (newNCB && newNCB !== prevNCB) {
      this.setState({ current_ncb_calculated: newNCB });
      // Removed info toast - NCB shown in UI
      console.log(`NCB automatically updated from ${prevNCB}% to ${newNCB}% for Roll Over`);
      return newNCB;
    }
    return prevNCB;
  };

  // Previous Insurer Validation (Excel Requirement)
  validatePreviousInsurer = (selectedInsurer) => {
    const restrictedInsurers = ["TATA AIG", "Tata AIG General Insurance Company Limited"];

    const isRestricted = restrictedInsurers.some(insurer =>
      selectedInsurer.toLowerCase().includes(insurer.toLowerCase())
    );

    if (isRestricted) {
      this.showToast("TATA AIG cannot be selected as previous insurer", "error");
      return false;
    }
    return true;
  };

  // Accessories Amount Validation (Excel Requirement)
  validateAccessoriesAmount = (amount, type) => {
    const maxLimit = 15000;

    if (amount > maxLimit) {
      this.showToast(`${type} accessories amount cannot exceed ₹${maxLimit.toLocaleString()}`, "error");
      return false;
    }
    return true;
  };

  // PUC Validation (Excel Requirement)
  validatePUCExpiry = (pucExpiryDate) => {
    if (!pucExpiryDate) return true;

    const pucDate = new Date(pucExpiryDate);
    const currentDate = new Date();
    const daysDifference = Math.ceil((pucDate - currentDate) / (1000 * 60 * 60 * 24));

    if (daysDifference > 180) {
      this.showToast("PUC expiry date should not be more than 180 days from today", "error");
      return false;
    }
    return true;
  };

  // Breaking Period Calculation (Excel SATP Requirements)
  calculateBreakingPeriod = (prevPolicyEndDate) => {
    if (!prevPolicyEndDate) return 0;

    const prevEndDate = new Date(prevPolicyEndDate);
    const currentDate = new Date();
    const daysDifference = Math.ceil((currentDate - prevEndDate) / (1000 * 60 * 60 * 24));

    this.setState({ breaking_period_days: daysDifference });

    // Only show error for critical case (> 365 days)
    if (daysDifference > 365) {
      this.showToast(`Breaking over 365 days (${daysDifference} days) - May affect eligibility`, "error");
    }
    // Removed info/warning toasts - breaking period shown in UI

    return daysDifference;
  };

  // Validate 90-day grace period for Roll Over
  validatePolicyStartDateGracePeriod = (prevPolicyEndDate, policyStartDate) => {
    if (!prevPolicyEndDate || !policyStartDate) return { isValid: true, message: "" };

    const prevEndDate = new Date(prevPolicyEndDate);
    const startDate = new Date(policyStartDate);

    // Calculate the difference in days
    const daysDifference = Math.ceil((startDate - prevEndDate) / (1000 * 60 * 60 * 24));

    // Policy start date should be within 1 to 90 days from previous policy end date
    if (daysDifference < 1) {
      return {
        isValid: false,
        message: "Policy start date must be after previous policy end date"
      };
    }

    if (daysDifference > 90) {
      return {
        isValid: false,
        message: `Policy start date exceeds 90-day grace period (${daysDifference} days gap). Maximum allowed is 90 days.`
      };
    }

    return {
      isValid: true,
      message: `Policy start date is within grace period (${daysDifference} days gap)`
    };
  };

  // SATP 60-Day Expiry Validation
  // If policy expired today, user can buy SATP up to 60 days from expiry date
  // On 61st day or beyond, no SATP quotes allowed
  validateSATPExpiryLimit = () => {
    const { plan_type, prev_pol_end_date, business_type } = this.state;

    // Check if this is a SATP (Standalone Third Party) plan
    const isSATP = plan_type && plan_type.toLowerCase().includes('standalone tp');

    // Only validate for SATP plans with Roll Over or Breaking business type
    if (!isSATP || !prev_pol_end_date) {
      return { isValid: true, message: "", daysExpired: 0 };
    }

    // Skip for New Business as they don't have previous policy
    if (business_type === "New Business") {
      return { isValid: true, message: "", daysExpired: 0 };
    }

    const prevEndDate = new Date(prev_pol_end_date);
    const today = new Date();

    // Reset time to midnight for accurate day calculation
    prevEndDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    // Calculate days since policy expired
    const daysSinceExpiry = Math.floor((today - prevEndDate) / (1000 * 60 * 60 * 24));

    // If policy hasn't expired yet (future date), allow
    if (daysSinceExpiry < 0) {
      return {
        isValid: true,
        message: `Policy not yet expired. Expires in ${Math.abs(daysSinceExpiry)} days.`,
        daysExpired: daysSinceExpiry
      };
    }

    // If policy expired today (day 0) or within 60 days, allow SATP
    // Day 0 = expired today, Day 60 = 60th day after expiry = allowed
    // Day 61+ = not allowed
    if (daysSinceExpiry <= 60) {
      const daysRemaining = 60 - daysSinceExpiry;
      return {
        isValid: true,
        message: `SATP allowed. Policy expired ${daysSinceExpiry} day(s) ago. ${daysRemaining} day(s) remaining to purchase SATP.`,
        daysExpired: daysSinceExpiry,
        daysRemaining: daysRemaining
      };
    }

    // Beyond 60 days - NO SATP quotes allowed
    return {
      isValid: false,
      message: `SATP NOT ALLOWED: Policy expired ${daysSinceExpiry} days ago (on ${prev_pol_end_date}). SATP can only be purchased within 60 days of policy expiry. Please select a different plan type.`,
      daysExpired: daysSinceExpiry
    };
  };

  // Zero Depreciation validation (Excel Requirement)
  validateZeroDep = () => {
    const zeroDepSelected = this.state.addon_values.dep_reimb || this.state.addon_values.dep_reimburse;

    if (zeroDepSelected && !this.state.prev_policy_had_zero_dep) {
      this.showToast("Previous policy must have Zero Depreciation when selecting Zero Dep cover", "error");
      return false;
    }
    return true;
  };

  // RTI validation (Excel Requirement)
  validateRTI = () => {
    const rtiSelected = this.state.addon_values.return_invoice || this.state.addon_values.rtn_invoice;

    if (rtiSelected && !this.state.prev_policy_had_rti) {
      this.showToast("Previous policy must have RTI when selecting Return to Invoice cover", "error");
      return false;
    }
    return true;
  };

  // Combined Zero Depreciation + RTI validation (for backward compatibility)
  validateZeroDepRTI = () => {
    return this.validateZeroDep() && this.validateRTI();
  };

  // SAOD CPA Mandatory Check (Excel SAOD Requirement)
  validateSAODCPA = () => {

    const paOwnerSelected = this.state.addon_values.pa_owner;
    const paOwnerTenure = this.state.addon_values.pa_owner_tenure;

    if (paOwnerSelected && (!paOwnerTenure || paOwnerTenure === "")) {
      this.showToast("Please select PA Cover Tenure", "error");
      return false;
    }
    return true;
  };

  // API Methods
  componentDidMount() {
    this.setState({ isLoading: true });

    Promise.all([
      this.Vehciledetailslistfetch(),
      this.MotorproductsfetchHandler(),
      this.MotoraddonstablefetchHandler()
    ]).then(() => {
      this.loadSavedAddonValues();
      this.setState({ isLoading: false });
    }).catch(error => {
      console.error("Error loading initial data:", error);
      this.setState({ isLoading: false });
    });
  }

  Vehciledetailslistfetch = () => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("sId", this.state.sId);
      formData.append("uId", this.state.uId);

      fetch(configData.api_url + "C_Tw_uat_master/manifacturerlist", {
        method: "POST",
        mode: "cors",
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === "success") {
            this.setState({ apiData: data.list });
            resolve(data);
          } else {
            reject(new Error("Failed to fetch vehicle data"));
          }
        })
        .catch((error) => {
          console.error("Error fetching vehicle list:", error);
          this.showToast("Failed to load vehicle data", "error");
          reject(error);
        });
    });
  };

  MotorproductsfetchHandler = () => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("sId", this.state.sId);
      formData.append("uId", this.state.uId);

      fetch(configData.api_url + 'C_Motorproducts/list', {
        method: 'POST',
        mode: 'cors',
        body: formData
      })
        .then(response => response.json())
        .then((data) => {
          if (data.status === "success") {
            console.log("Motor products loaded:", data.list);
            this.setState({ motorproducts: data.list });
            resolve(data);
          } else {
            reject(new Error("Failed to fetch motor products"));
          }
        })
        .catch((error) => {
          console.error("Fetch error:", error);
          this.showToast("Failed to load motor products", "error");
          reject(error);
        });
    });
  };

  MotoraddonstablefetchHandler = () => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("sId", this.state.sId);
      formData.append("uId", this.state.uId);

      fetch(configData.api_url + 'C_Motoraddonstable/list', {
        method: 'POST',
        mode: 'cors',
        body: formData
      })
        .then(response => response.json())
        .then((data) => {
          if (data.status === "success") {
            console.log("Motor addons loaded:", data.list);
            this.setState({ motoraddons: data.list });
            resolve(data);
          } else {
            reject(new Error("Failed to fetch motor addons"));
          }
        })
        .catch((error) => {
          console.error("Fetch error:", error);
          this.showToast("Failed to load add-ons data", "error");
          reject(error);
        });
    });
  };

  // Policy Plan Details
  getPolicyPlanDetails = (planType) => {
    if (!planType) return { id: 1, tenure: "1" };

    if (POLICY_PLAN_MAPPING[planType]) {
      return POLICY_PLAN_MAPPING[planType];
    }

    const normalizedPlan = planType.replace(/[()]/g, '').trim();
    for (const [key, value] of Object.entries(POLICY_PLAN_MAPPING)) {
      const normalizedKey = key.replace(/[()]/g, '').trim();
      if (normalizedKey === normalizedPlan) {
        return value;
      }
    }

    return { id: 1, tenure: "1" };
  };

  getTenureFromPlan = (plan) => {
    if (!plan) return "1";

    const tenureMatch = plan.toLowerCase().match(/(\d+)\s*years?/);
    if (tenureMatch && ['1', '2', '3', '5'].includes(tenureMatch[1])) {
      return tenureMatch[1];
    }

    return "1";
  };

  // Addon Management
  filterAddonsByPlan = (planType) => {
    if (!planType || !this.state.motoraddons.length) {
      return [];
    }

    const planLower = planType.toLowerCase();
    const isSAOD = planLower.includes('standalone od') || planLower.includes('saod');
    const isSATP = planLower.includes('standalone tp') || planLower.includes('satp');
    const isPackage = planLower.includes('package');

    const planVariations = [
      planType,
      planType.replace(/\s+/g, '_'),
      planType.replace(/\s+/g, ''),
      planType.toLowerCase(),
      planType.toLowerCase().replace(/\s+/g, '_'),
      planType.replace(/[()]/g, ''),
      planType.replace(/[()]/g, '').replace(/\s+/g, '_')
    ];

    const planMatchedAddons = this.state.motoraddons.filter(addon => {
      const addonPlanFields = [
        addon.productName,
        addon.baseProductId,
        addon.planType,
        addon.product_name,
        addon.plan_name,
        addon.applicableFor
      ];

      return addonPlanFields.some(field =>
        field && planVariations.some(variation =>
          field.toLowerCase().includes(variation.toLowerCase()) ||
          variation.toLowerCase().includes(field.toLowerCase()) ||
          field === variation
        )
      );
    });

    // ✅ SAOD Filter: Filter add-ons for Standalone OD
    // ❌ NOT Allowed: TPPD, PA Covers, Legal Liability
    let filteredByPlanType = planMatchedAddons;
    if (isSAOD) {
      filteredByPlanType = planMatchedAddons.filter(addon => {
        const addonKey = (addon.addons || addon.addon_name || addon.name || '').toLowerCase();
        const isAllowed = SAOD_ALLOWED_ADDONS.some(allowed => addonKey.includes(allowed.toLowerCase()));
        if (!isAllowed) {
          console.log(`SAOD Filter: ${addonKey} not allowed for Standalone OD`);
        }
        return isAllowed;
      });
      console.log(`SAOD: Filtered ${planMatchedAddons.length} addons to ${filteredByPlanType.length} allowed addons`);
    }

    // ✅ SATP Filter: Filter add-ons for Standalone TP
    // ❌ NOT Allowed: Dep, RTI, Consumable, Towing, RSA, EME, Discounts, Legal Liability
    if (isSATP) {
      filteredByPlanType = planMatchedAddons.filter(addon => {
        const addonKey = (addon.addons || addon.addon_name || addon.name || '').toLowerCase();
        const isAllowed = SATP_ALLOWED_ADDONS.some(allowed => addonKey.includes(allowed.toLowerCase()));
        if (!isAllowed) {
          console.log(`SATP Filter: ${addonKey} not allowed for Standalone TP`);
        }
        return isAllowed;
      });
      console.log(`SATP: Filtered ${planMatchedAddons.length} addons to ${filteredByPlanType.length} allowed addons`);
    }

    // ✅ Package Filter: Filter add-ons for Package policies
    // ❌ NOT Allowed: Legal Liability IMT 28 & 29
    if (isPackage) {
      filteredByPlanType = planMatchedAddons.filter(addon => {
        const addonKey = (addon.addons || addon.addon_name || addon.name || '').toLowerCase();
        const isAllowed = PACKAGE_ALLOWED_ADDONS.some(allowed => addonKey.includes(allowed.toLowerCase()));
        if (!isAllowed) {
          console.log(`Package Filter: ${addonKey} not allowed for Package policy`);
        }
        return isAllowed;
      });
      console.log(`Package: Filtered ${planMatchedAddons.length} addons to ${filteredByPlanType.length} allowed addons`);
    }

    const ageEligibleAddons = filteredByPlanType.filter(addon => {
      if (!addon.maxAge) return true;

      const maxAllowedAge = this.parseMaxAge(addon.maxAge);
      const isEligible = this.state.vehicle_age <= maxAllowedAge;

      if (!isEligible) {
        console.log(`Addon ${addon.addons} filtered out due to age: ${this.state.vehicle_age} > ${maxAllowedAge}`);
      }

      return isEligible;
    });

    return ageEligibleAddons;
  };

  // ✅ Check if add-on with 5-year condition is eligible
  // 5-year condition applies to: Zero Dep, Consumable, RSA, EME
  isFiveYearAddonEligible = (addonKey) => {
    const vehicleAge = this.state.precise_vehicle_age?.totalInYears || this.state.vehicle_age || 0;
    const isFiveYearAddon = FIVE_YEAR_CONDITION_ADDONS.some(addon =>
      addonKey.toLowerCase().includes(addon.toLowerCase())
    );

    if (isFiveYearAddon && vehicleAge > FIVE_YEAR_ADDON_MAX_AGE) {
      console.log(`5-Year Addon not eligible: ${addonKey} - Vehicle age ${vehicleAge.toFixed(2)} years > 5 years limit`);
      return false;
    }
    return true;
  };

  // ✅ Check if RTI (Return to Invoice) is eligible based on vehicle age
  // RTI eligibility: Vehicle Registration Age must be ≤ 3 years
  isRTIEligible = () => {
    const vehicleAge = this.state.precise_vehicle_age?.totalInYears || this.state.vehicle_age || 0;
    const isEligible = vehicleAge <= RTI_MAX_AGE_YEARS;

    if (!isEligible) {
      console.log(`RTI not eligible: Vehicle age ${vehicleAge.toFixed(2)} years > 3 years 08 months 28 days`);
    }
    return isEligible;
  };

  parseMaxAge = (maxAgeString) => {
    if (!maxAgeString) return Infinity;
    const match = maxAgeString.match(/(\d+)/);
    return match ? parseInt(match[1]) : Infinity;
  };

  loadSavedAddonValues = () => {
    const savedAddonValues = sessionStorage.getItem('addon_values');
    const savedPlanType = sessionStorage.getItem('plan_type');

    if (savedAddonValues && savedPlanType) {
      try {
        const addonValues = JSON.parse(savedAddonValues);
        this.setState({
          addon_values: addonValues,
          plan_type: savedPlanType
        });

        const filteredAddons = this.filterAddonsByPlan(savedPlanType);
        this.setState({ filtered_addons: filteredAddons });
      } catch (error) {
        console.error('Error loading saved addon values:', error);
      }
    }
  };

  setDefaultAddonValues = (addons) => {
    const defaultValues = {};
    const shouldShowPA = this.shouldShowPAOwner();

    addons.forEach(addon => {
      const addonKey = addon.addons || addon.addon_name || addon.name;

      if (addonKey === 'pa_owner' && !shouldShowPA) {
        defaultValues[addonKey] = false;
        return;
      }

      if (addonKey === 'pa_owner_declaration') {
        if (shouldShowPA) {
          const paOwnerAddon = addons.find(a => (a.addons || a.addon_name || a.name) === 'pa_owner');
          defaultValues[addonKey] = paOwnerAddon?.defaultValue === 'true'
            ? "None"
            : "Owner driver does not hold valid Driving license";
        } else {
          defaultValues[addonKey] = "None";
        }
      } else if (addonKey === 'pa_owner_tenure') {
        defaultValues[addonKey] = "";
      } else if (addon.defaultValue) {
        if (addon.addons_type === 'checkbox') {
          defaultValues[addonKey] = addon.defaultValue.toLowerCase() === 'true';
        } else {
          defaultValues[addonKey] = addon.defaultValue;
        }
      } else {
        defaultValues[addonKey] = addon.addons_type === 'checkbox' ? false : '';
      }
    });

    this.setState({ addon_values: defaultValues });
  };

  shouldShowPAOwner = () => {
    return this.state.proposer_type === "Individual";
  };

  getPAOwnerTenureOptions = () => {
    const { business_type } = this.state;

    if (business_type === "Roll Over") {
      return [
        { value: '1', label: '1 Year' },
        { value: '2', label: '2 Years' },
        { value: '3', label: '3 Years' },
        { value: '4', label: '4 Years' },
        { value: '5', label: '5 Years' }
      ];
    }

    return [];
  };

  getPAOwnerDeclarationOptions = () => {
    return [
      {
        value: "Owner driver does not hold valid Driving license",
        label: "Owner driver does not hold valid Driving license"
      },
      {
        value: "Insured has any other motor vehicle policy covering PA",
        label: "Insured has any other motor vehicle policy covering PA"
      },
      {
        value: "Insured has standalone PA cover >= 15 lakhs",
        label: "Insured has standalone PA cover >= 15 lakhs"
      }
    ];
  };

  // Form Handlers
  handlebusiness_typeChange = (e) => {
    const fieldValue = e.target.value;
    this.setState({
      business_type: fieldValue,
      plan_type: "",
      disclaimer_accepted: false,
      filtered_addons: [],
      addon_values: {},
      current_ncb_calculated: "0"
    });

    // Don't auto-set date here - will be set when plan is selected
    // if (fieldValue === "New Business" || fieldValue === "Breaking") {
    //   const tomorrow = new Date();
    //   tomorrow.setDate(tomorrow.getDate() + 1);
    //   const tomorrowFormatted = this.formatDate(tomorrow);
    //   this.setState({ pol_start_date: tomorrowFormatted });
    //   this.showToast("Policy start date set to T+1 for SATP", "info");
    // }

    if (fieldValue === "Roll Over" && this.state.pre_pol_ncb) {
      this.handleNCBAutoIncrement(this.state.pre_pol_ncb, fieldValue);
    }

    if (this.state.vehicle_age > 0) {
      this.checkPlanEligibility(this.state.vehicle_age);
    }
  };

  handleproposer_typeChange = (e) => {
    const fieldValue = e.target.value;
    this.setState({ proposer_type: fieldValue }, () => {
      const shouldShowPA = this.shouldShowPAOwner();

      if (!shouldShowPA) {
        this.setState(prevState => ({
          addon_values: {
            ...prevState.addon_values,
            pa_owner: false,
            pa_owner_tenure: "",
            pa_owner_declaration: "None"
          }
        }));
        // Removed info toast - silent operation
      } else {
        this.setState(prevState => ({
          addon_values: {
            ...prevState.addon_values,
            pa_owner: false,
            pa_owner_tenure: "",
            pa_owner_declaration: "Owner driver does not hold valid Driving license"
          }
        }));
      }
    });
  };

  handleplanChange = (e) => {
    const selectedPlan = e.target.value;

    if (!selectedPlan) {
      this.setState({
        plan_type: "",
        filtered_addons: [],
        addon_values: {},
        isTPDisabled: false,
      });
      return;
    }

    // ✅ SAOD 4-year restriction check
    // ✅ Rule 2: SAOD Vehicle Age Rule - Up to 4 years 08 months 28 days (4.74 years)
    if (selectedPlan.toLowerCase().includes('standalone od') || selectedPlan.toLowerCase().includes('saod')) {
      const vehicleAge = this.state.precise_vehicle_age?.totalInYears || this.state.vehicle_age || this.calculateVehicleAge(this.state.dor);
      if (vehicleAge > 4.74) { // 4 years 08 months 28 days
        const preciseDisplay = this.formatPreciseVehicleAge(this.state.precise_vehicle_age);
        this.showToast(
          `SAOD plan is only available for vehicles up to 4 years 08 months 28 days. Current vehicle age: ${preciseDisplay}`,
          "error"
        );
        return;
      }
    }

    if (this.state.dor &&
      !this.isVehicleEligibleForPlanPrecise(this.state.dor, this.state.business_type, selectedPlan)) {
      const preciseDisplay = this.formatPreciseVehicleAge(this.state.precise_vehicle_age);
      this.showToast(
        `Vehicle (${preciseDisplay} old) is not eligible for ${selectedPlan} with ${this.state.business_type} business type`,
        "error"
      );
      return;
    }
    const isTPDisabled =
      selectedPlan.toLowerCase().includes("package") ||
      selectedPlan.toLowerCase().includes("saod") ||
      selectedPlan.toLowerCase().includes("standalone od");

    this.setState({
      plan_type: selectedPlan,
      isTPDisabled,
      // clear TP if already selected
      prev_policy_type:
        isTPDisabled && this.state.prev_policy_type === "TP"
          ? ""
          : this.state.prev_policy_type,
    });

    const policyDetails = this.getPolicyPlanDetails(selectedPlan);
    console.log("Selected Plan:", selectedPlan);
    console.log("Policy Plan Details:", policyDetails);

    // Check if this is a SATP plan and auto-set policy start date to T+1
    const isSATP = selectedPlan.toLowerCase().includes('standalone tp');
    const isSAOD = selectedPlan.toLowerCase().includes('standalone od') || selectedPlan.toLowerCase().includes('saod');

    // Determine the correct policy start date based on plan type
    let newPolicyStartDate = this.state.pol_start_date;

    if (isSAOD) {
      // SAOD - allow today (T+0)
      const today = new Date();
      newPolicyStartDate = this.formatDate(today);
    } else if (isSATP) {
      // SATP - require T+1
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      newPolicyStartDate = this.formatDate(tomorrow);
    }

    const filteredAddons = this.filterAddonsByPlan(selectedPlan);

    // Calculate policy dates using the NEW start date
    let policyEndDate = this.state.pol_end_date;
    let tpEndDate = this.state.tp_end_date;

    if (newPolicyStartDate && selectedPlan) {
      const calculatedDates = this.calculatePolicyDates(selectedPlan, newPolicyStartDate);
      if (calculatedDates) {
        policyEndDate = calculatedDates.policyEndDate;
        tpEndDate = calculatedDates.tpEndDate;
      }
    }

    // Single consolidated setState to avoid race conditions
    this.setState({
      pol_start_date: newPolicyStartDate,
      pol_end_date: policyEndDate,
      tp_end_date: tpEndDate,
      filtered_addons: filteredAddons,
      addon_values: {}
    }, () => {
      // Validate SATP 60-day limit after state update - only show ERROR
      if (isSATP) {
        const satpValidation = this.validateSATPExpiryLimit();
        if (!satpValidation.isValid) {
          this.showToast(satpValidation.message, "error");
        }
      }
    });

    this.setDefaultAddonValues(filteredAddons);

    sessionStorage.setItem('plan_type', selectedPlan);
    sessionStorage.setItem('filtered_addons', JSON.stringify(filteredAddons));
  };

  calculatePolicyDates = (planType, policyStartDate) => {
    if (!planType || !policyStartDate) return null;

    const startDate = new Date(policyStartDate);
    const years = parseInt(this.getTenureFromPlan(planType));
    const tpEndDate = new Date(startDate.getFullYear() + years, startDate.getMonth(), startDate.getDate() - 1);

    return {
      policyStartDate: policyStartDate,
      policyEndDate: this.formatDate(tpEndDate),
      tpEndDate: this.formatDate(tpEndDate)
    };
  };

  // Date Handlers
  handledordatesChange = (date) => {
    if (date) {
      const formattedDate = this.formatDate(date);
      this.setState({
        dor: formattedDate,
        man_year: date.getFullYear().toString(),
        manu_month: (date.getMonth() + 1).toString().padStart(2, "0")
      });

      this.updateVehicleAgeInfo(formattedDate);
    }
  };

  handlestartdatesChange = (date) => {
    if (date) {
      const formattedDate = this.formatDate(date);
      const { plan_type, business_type, prev_pol_end_date } = this.state;

      // Check if SAOD plan
      const isSAOD = plan_type && (plan_type.toLowerCase().includes('standalone od') || plan_type.toLowerCase().includes('saod'));

      // For SAOD - allow today or any future date, skip Roll Over prev_pol_end_date check
      if (isSAOD) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(formattedDate);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate.getTime() < today.getTime()) {
          this.showToast("SAOD policy start date must be today or a future date", "error");
          return;
        }
        // SAOD can use today's date - proceed without Roll Over check
      } else if (business_type === "Roll Over" && prev_pol_end_date) {
        // Non-SAOD Roll Over - check prev policy end date
        const prevEndDate = new Date(prev_pol_end_date);
        const startDate = new Date(formattedDate);
        const daysDifference = Math.ceil((startDate - prevEndDate) / (1000 * 60 * 60 * 24));

        if (daysDifference < 1) {
          this.showToast("Policy start date must be after previous policy end date", "error");
          return;
        }
      }

      this.setState({ pol_start_date: formattedDate });

      // Calculate end dates silently without toast
      if (this.state.plan_type) {
        const calculatedDates = this.calculatePolicyDates(this.state.plan_type, formattedDate);
        if (calculatedDates) {
          this.setState({
            pol_end_date: calculatedDates.policyEndDate,
            tp_end_date: calculatedDates.tpEndDate
          });
          // Removed toast - end date calculation doesn't need notification
        }
      }
    }
  };

  handledatesChange = (date) => {
    if (date) {
      this.setState({ prev_pol_start_date: this.formatDate(date) });
    }
  };

  handledateEndChange = (date) => {
    if (date) {
      const formattedDate = this.formatDate(date);
      this.setState({
        prev_pol_end_date: formattedDate,
        hasPreviousPolicy: true

      }, () => {
        // Validate SATP 60-day limit after state update - only show ERROR, not info/warning
        const isSATP = this.state.plan_type && this.state.plan_type.toLowerCase().includes('standalone tp');
        if (isSATP) {
          const satpValidation = this.validateSATPExpiryLimit();
          if (!satpValidation.isValid) {
            this.showToast(satpValidation.message, "error");
          }
          // Removed info/warning toasts - only show error when SATP is not allowed
        }
      });

      // Calculate breaking period silently - only show error if > 365 days
      if (this.state.business_type === "Breaking") {
        this.calculateBreakingPeriodSilent(formattedDate);
      }
    }
  };

  // Silent breaking period calculation - only shows error for > 365 days
  calculateBreakingPeriodSilent = (prevPolicyEndDate) => {
    if (!prevPolicyEndDate) return 0;

    const prevEndDate = new Date(prevPolicyEndDate);
    const currentDate = new Date();
    const daysDifference = Math.ceil((currentDate - prevEndDate) / (1000 * 60 * 60 * 24));

    this.setState({ breaking_period_days: daysDifference });

    // Only show toast for critical error (> 365 days)
    if (daysDifference > 365) {
      this.showToast(`Breaking over 365 days (${daysDifference} days) - May not be eligible`, "error");
    }

    return daysDifference;
  };

  handlebledatesChange = (date) => {
    if (date) this.setState({ bletpstart: this.formatDate(date) });
  };

  handleenddatesChange = (date) => {
    if (date) this.setState({ bletpend: this.formatDate(date) });
  };

  handleoddatesChange = (date) => {
    if (date) this.setState({ bleodstart: this.formatDate(date) });
  };

  handleodenddatesChange = (date) => {
    if (date) this.setState({ bleodend: this.formatDate(date) });
  };

  handleFutureExpiryDateChange = (date) => {
    if (date) {
      this.setState({ future_expiry_date: this.formatDate(date) });
    }
  };

  handlePUCExpiryChange = (date) => {
    if (date) {
      const formattedDate = this.formatDate(date);
      if (this.validatePUCExpiry(formattedDate)) {
        this.setState({ puc_expiry_date: formattedDate });
      }
    }
  };

  handleCPAStartDateChange = (date) => {
    if (date) {
      const formattedDate = this.formatDate(date);
      this.setState({ cpa_start_date: formattedDate }, () => {
        // Auto-calculate CPA end date based on tenure if tenure is selected
        const paTenure = this.state.addon_values.pa_owner_tenure;
        if (paTenure && date) {
          const endDate = new Date(date);
          endDate.setFullYear(endDate.getFullYear() + parseInt(paTenure));
          endDate.setDate(endDate.getDate() - 1); // End date is 1 day before anniversary
          this.setState({ cpa_end_date: this.formatDate(endDate) });
          // Removed success toast - CPA dates shown in UI
        }
      });
    }
  };

  handleCPAEndDateChange = (date) => {
    if (date) {
      const formattedDate = this.formatDate(date);
      this.setState({ cpa_end_date: formattedDate });
    }
  };

  handleDisclaimerAcceptance = (e) => {
    this.setState({ disclaimer_accepted: e.target.checked });
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      [name]: name === "registrationNumber" ? value.toUpperCase() : value,
    });
  };

  handleAddonValueChange = (addonName, value, addonType) => {
    this.setState(prevState => ({
      addon_values: {
        ...prevState.addon_values,
        [addonName]: value
      }
    }), () => {
      if (addonName === 'pa_owner') {
        if (value) {
          this.setState(prevState => ({
            addon_values: {
              ...prevState.addon_values,
              pa_owner_declaration: "None",
              pa_owner_tenure: ""
            }
          }));
          // Removed info toast - silent operation
        } else {
          this.setState(prevState => ({
            addon_values: {
              ...prevState.addon_values,
              pa_owner_declaration: "Owner driver does not hold valid Driving license",
              pa_owner_tenure: ""
            }
          }));
          // Removed info toast - silent operation
        }
      }

      // Reset "Previous policy had Zero Depreciation" when Zero Depreciation Cover is unchecked
      if (addonName === 'dep_reimburse' && !value) {
        this.setState({
          prev_policy_had_zero_dep: false
        });
      }

      // Reset "Previous policy had RTI" when RTI is unchecked
      if (addonName === 'return_invoice' && !value) {
        this.setState({
          prev_policy_had_rti: false
        });
      }

      // ✅ Rule 4: RTI (Return to Invoice) Age Validation
      // RTI eligibility: Vehicle Registration Age must be ≤ 3 years 08 months 28 days
      // ✅ RTI (Return to Invoice) - 3 year condition
      if ((addonName === 'return_invoice' || addonName === 'rtn_invoice') && value) {
        if (!this.isRTIEligible()) {
          const preciseDisplay = this.formatPreciseVehicleAge(this.state.precise_vehicle_age);
          this.showToast(
            `RTI NOT ALLOWED: Vehicle age ${preciseDisplay} exceeds  3 years 08 months 28 days  limit. RTI will be rejected.`,
            "error"
          );
          // Auto-uncheck RTI if vehicle is not eligible
          setTimeout(() => {
            this.setState(prevState => ({
              addon_values: {
                ...prevState.addon_values,
                [addonName]: false
              }
            }));
          }, 100);
        }
      }

      // ✅ 5-Year Condition Add-ons (Zero Dep, Consumable, RSA, EME)
      const fiveYearAddons = ['dep_reimburse', 'dep_reimb', 'consumbale_expense', 'consumable', 'rsa', 'emg_med_exp'];
      if (fiveYearAddons.includes(addonName) && value) {
        if (!this.isFiveYearAddonEligible(addonName)) {
          const preciseDisplay = this.formatPreciseVehicleAge(this.state.precise_vehicle_age);
          this.showToast(
            `${this.formatAddonLabel(addonName)} NOT ALLOWED: Vehicle age ${preciseDisplay} exceeds 5 years limit.`,
            "error"
          );
          // Auto-uncheck addon if vehicle is not eligible
          setTimeout(() => {
            this.setState(prevState => ({
              addon_values: {
                ...prevState.addon_values,
                [addonName]: false
              }
            }));
          }, 100);
        }
      }
    });

    // Removed addon enabled/disabled toast - too many notifications
  };

  handlePreviousInsurerChange = (e) => {
    const selectedInsurer = e.target.value;

    if (selectedInsurer && !this.validatePreviousInsurer(selectedInsurer)) {
      e.target.value = "";
      this.setState({ prev_insurer: "" });
    } else {
      this.setState({ prev_insurer: selectedInsurer });
    }
  };

  handleNCBChange = (e) => {
    const prevNCB = e.target.value;
    this.setState({ pre_pol_ncb: prevNCB });

    if (this.state.business_type === "Roll Over") {
      this.handleNCBAutoIncrement(prevNCB, this.state.business_type);
    }
  };

  // Registration Number Handlers
  handleRegNo1Change = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z]/g, "");
    if (value.length <= 2) {
      this.setState({ regno_1: value });
    }
  };

  handleRegNo2Change = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 2) {
      this.setState({ regno_2: value });
    }
  };

  handleRegNo3Change = (e) => {
    let value = e.target.value.toUpperCase().replace(/[^A-Z]/g, "");
    if (value.length <= 2) {
      this.setState({ regno_3: value });
    }
  };

  handleRegNo4Change = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 4) {
      this.setState({ regno_4: value });
    }
  };

  // Vehicle Selection Handlers
  handleMakeChange = (selectedOption) => {
    const selectedMake = selectedOption.value;
    const models = this.state.apiData[selectedMake] || [];
    const makeCode = models.length > 0 ? models[0].num_manufacturercode.toString() : "";

    this.setState({
      vehicle_make: selectedOption,
      models,
      vehicle_model: null,
      vehicle_variant: null,
      variants: [],
      make_code: makeCode
    });
  };

  handleModelChange = (selectedOption) => {
    const selectedModel = selectedOption.value;
    const { models } = this.state;
    const variants = models.filter(m => m.txt_model_name === selectedModel);
    const modelCode = variants.length > 0 ? variants[0].num_model_code.toString() : "";

    this.setState({
      vehicle_model: selectedOption,
      variants,
      vehicle_variant: null,
      model_code: modelCode
    });
  };

  handleVariantChange = (selectedOption) => {
    const { variants } = this.state;
    const variant = variants.find(v => v.txt_model_variant === selectedOption.value);

    this.setState({
      vehicle_variant: selectedOption,
      variant_code: variant ? variant.num_model_variant_code.toString() : ""
    });
  };

  // Enhanced Validation Methods with Excel Requirements
  validateExcelRequirements = () => {
    const validations = [];

    // 1. Vehicle Age Precise Validation (Excel Req #1)
    // ✅ Includes SAOD 4 years 08 months 28 days restriction (Rule 2)
    if (this.state.dor && this.state.plan_type && this.state.business_type) {
      const isEligible = this.isVehicleEligibleForPlanPrecise(
        this.state.dor,
        this.state.business_type,
        this.state.plan_type
      );
      if (!isEligible) {
        const preciseDisplay = this.formatPreciseVehicleAge(this.state.precise_vehicle_age);
        const planType = this.state.plan_type.toLowerCase();
        if (planType.includes('standalone od') || planType.includes('saod')) {
          validations.push(`SAOD plan requires vehicle age ≤ 4 years 08 months 28 days. Current: ${preciseDisplay}`);
        } else {
          validations.push(`Vehicle age ${preciseDisplay} exceeds the allowed limit for selected plan`);
        }
      }
    }

    // 2. RTI (Return to Invoice) Age Validation - 3 year condition
    // RTI eligibility: Vehicle Registration Age must be ≤ 3 years
    const rtiSelected = this.state.addon_values.return_invoice || this.state.addon_values.rtn_invoice;
    if (rtiSelected && !this.isRTIEligible()) {
      const preciseDisplay = this.formatPreciseVehicleAge(this.state.precise_vehicle_age);
      validations.push(`RETURN TO INVOICE (RTI) not allowed: Vehicle age ${preciseDisplay} exceeds 3 years limit`);
    }

    // 2a. 5-Year Condition Add-ons Validation
    // Zero Dep, Consumable, RSA, EME - Vehicle age must be ≤ 5 years
    const vehicleAge = this.state.precise_vehicle_age?.totalInYears || this.state.vehicle_age || 0;
    const zeroDepSelected = this.state.addon_values.dep_reimb || this.state.addon_values.dep_reimburse;
    const consumableSelected = this.state.addon_values.consumbale_expense || this.state.addon_values.consumable;
    const rsaSelected = this.state.addon_values.rsa;
    const emeSelected = this.state.addon_values.emg_med_exp;

    if (vehicleAge > FIVE_YEAR_ADDON_MAX_AGE) {
      const preciseDisplay = this.formatPreciseVehicleAge(this.state.precise_vehicle_age);
      if (zeroDepSelected) {
        validations.push(`ZERO DEPRECIATION not allowed: Vehicle age ${preciseDisplay} exceeds 5 years limit`);
      }
      if (consumableSelected) {
        validations.push(`CONSUMABLE EXPENSES not allowed: Vehicle age ${preciseDisplay} exceeds 5 years limit`);
      }
      if (rsaSelected) {
        validations.push(`ROAD SIDE ASSISTANCE not allowed: Vehicle age ${preciseDisplay} exceeds 5 years limit`);
      }
      if (emeSelected) {
        validations.push(`EMERGENCY MEDICAL EXPENSES not allowed: Vehicle age ${preciseDisplay} exceeds 5 years limit`);
      }
    }

    // 3. Zero Depreciation Validation (Excel Req #3)
    if (zeroDepSelected && !this.state.prev_policy_had_zero_dep) {
      validations.push("ZERO DEPRECIATION COVER selected - You MUST confirm that Previous Policy had Zero Depreciation");
    }

    // 3a. RTI Validation - Independent check (rtiSelected already declared above)
    if (rtiSelected && !this.state.prev_policy_had_rti) {
      validations.push("RETURN TO INVOICE (RTI) selected - You MUST confirm that Previous Policy had RTI cover");
    }

    // 4. Accessories Limit Validation (Excel Req #4)
    if (this.state.addon_values.electrical_si && this.state.addon_values.electrical_si > 15000) {
      validations.push("Electrical accessories amount exceeds ₹15,000 limit");
    }
    if (this.state.addon_values.non_electrical_si && this.state.addon_values.non_electrical_si > 15000) {
      validations.push("Non-electrical accessories amount exceeds ₹15,000 limit");
    }


    if (!this.validateSAODCPA()) {
      validations.push("Please select PA Cover Tenure");
    }
    // 6. Previous Insurer Validation (Excel Req #7 & SATP #5)
    if (this.state.prev_insurer && this.state.prev_insurer.toLowerCase().includes('tata aig')) {
      validations.push("TATA AIG cannot be selected as previous insurer");
    }

    // 7. Terms & Conditions Validation (Excel Req #8)

    // 8. PUC Validation (Excel Req #10)
    if (this.state.puc_expiry_date && !this.validatePUCExpiry(this.state.puc_expiry_date)) {
      validations.push("PUC expiry date should be within 180 days from today");
    }

    // 9. SATP 60-Day Expiry Limit Validation
    // SATP can only be purchased within 60 days of policy expiry
    const satpExpiryValidation = this.validateSATPExpiryLimit();
    if (!satpExpiryValidation.isValid) {
      validations.push(satpExpiryValidation.message);
    }

    return {
      isValid: validations.length === 0,
      errors: validations
    };
  };

  validateVehicleDetails = () => {
    const requiredFields = [
      { field: 'vehicle_make', label: 'Vehicle Make' },
      { field: 'vehicle_model', label: 'Vehicle Model' },
      { field: 'vehicle_variant', label: 'Vehicle Variant' },
      { field: 'dor', label: 'Date of Registration' }
    ];

    const missingFields = requiredFields.filter(item => {
      const value = this.state[item.field];
      return !value || value === "" || (typeof value === 'object' && !value.label);
    });

    if (missingFields.length > 0) {
      return {
        isValid: false,
        message: `Please select: ${missingFields.map(item => item.label).join(', ')}`
      };
    }

    if (!this.state.make_code || !this.state.model_code || !this.state.variant_code) {
      return {
        isValid: false,
        message: "Vehicle codes are missing. Please reselect vehicle make, model, and variant."
      };
    }

    return { isValid: true };
  };

  validateRequiredAddons = () => {
    const shouldShowPA = this.shouldShowPAOwner();
    const requiredAddons = this.state.filtered_addons.filter(addon =>
      addon.defaultValue && addon.defaultValue.toLowerCase() === 'true'
    );

    for (let addon of requiredAddons) {
      const addonKey = addon.addons || addon.addon_name || addon.name;
      const value = this.state.addon_values[addonKey];

      if (addonKey === 'pa_owner' && !shouldShowPA) {
        continue;
      }

      if (addon.addons_type === 'checkbox' && !value) {
        return {
          isValid: false,
          message: `${this.formatAddonLabel(addonKey)} is required`
        };
      }

      if ((addon.addons_type === 'select' || addon.addons_type === 'textbox') && (!value || value.trim() === '')) {
        return {
          isValid: false,
          message: `${this.formatAddonLabel(addonKey)} is required`
        };
      }
    }

    if (shouldShowPA) {
      const paOwner = this.state.addon_values.pa_owner;
      const paDeclaration = this.state.addon_values.pa_owner_declaration;
      const paTenure = this.state.addon_values.pa_owner_tenure;

      // if (!paDeclaration || paDeclaration.trim() === '') {
      //   return {
      //     isValid: false,
      //     message: "Owner Driver Declaration is mandatory"
      //   };
      // }

      if (paOwner && (!paTenure || paTenure.trim() === '')) {
        return {
          isValid: false,
          message: "CPA Tenure is required when Compulsory Personal Accident is selected"
        };
      }

      // Validate CPA dates when PA Owner is selected
      if (paOwner) {
        if (!this.state.cpa_start_date || this.state.cpa_start_date.trim() === '') {
          return {
            isValid: false,
            message: "CPA Start Date is required when Compulsory Personal Accident is selected"
          };
        }

        if (!this.state.cpa_end_date || this.state.cpa_end_date.trim() === '') {
          return {
            isValid: false,
            message: "CPA End Date is required when Compulsory Personal Accident is selected"
          };
        }

        // Validate that end date is after start date
        const startDate = new Date(this.state.cpa_start_date);
        const endDate = new Date(this.state.cpa_end_date);

        if (endDate <= startDate) {
          return {
            isValid: false,
            message: "CPA End Date must be after CPA Start Date"
          };
        }
      }
    }

    return { isValid: true };
  };

  validateSATPPolicyStartDate = () => {
    const { business_type, pol_start_date, plan_type } = this.state;

    // Check plan types
    const isSATP = plan_type && plan_type.toLowerCase().includes('standalone tp');
    const isSAOD = plan_type && (plan_type.toLowerCase().includes('standalone od') || plan_type.toLowerCase().includes('saod'));

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayFormatted = this.formatDate(today);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const tomorrowFormatted = this.formatDate(tomorrow);

    // SAOD - allows today (T+0) for all business types
    if (isSAOD) {
      const selectedDate = new Date(pol_start_date);
      selectedDate.setHours(0, 0, 0, 0);
      // Allow today or any future date
      if (selectedDate.getTime() < today.getTime()) {
        return {
          isValid: false,
          message: `SAOD policy start date must be today (${todayFormatted}) or later`
        };
      }
      return { isValid: true };
    }

    if (isSATP) {
      // For SATP with Roll Over/Breaking - allow any date from T+1 onwards (within 60-day window)
      // For SATP New Business - must be exactly T+1
      if (business_type === "New Business") {
        if (pol_start_date !== tomorrowFormatted) {
          return {
            isValid: false,
            message: `For SATP New Business, policy start date must be T+1 (Tomorrow: ${tomorrowFormatted})`
          };
        }
      } else {
        // Roll Over or Breaking - just ensure date is T+1 or later
        const selectedDate = new Date(pol_start_date);
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < tomorrow) {
          return {
            isValid: false,
            message: `SATP policy start date must be T+1 (${tomorrowFormatted}) or later`
          };
        }
      }
    } else if (business_type === "New Business") {
      // Non-SATP/SAOD New Business - must be T+1
      if (pol_start_date !== tomorrowFormatted) {
        return {
          isValid: false,
          message: "For New Business, policy start date must be T+1 (Tomorrow)"
        };
      }
    }
    // For non-SATP Roll Over/Breaking - no strict T+1 requirement

    return { isValid: true };
  };

  // Render Helper Methods
  formatAddonLabel = (addonName) => {
    const labelMap = {
      // PA Covers
      'pa_owner': 'COMPULSORY PERSONAL ACCIDENT (CPA)',
      'pa_owner_tenure': 'CPA TENURE',
      'pa_owner_declaration': 'OWNER DRIVER DECLARATION',
      'pa_paid': 'PA COVERED PAID DRIVERS',
      'pa_paid_no': 'NUMBER OF PAID DRIVERS',
      'pa_paid_si': 'SUM INSURED FOR PAID DRIVERS',
      'pa_unnamed': 'PA UNNAMED PASSENGER',
      'pa_unnamed_no': 'NUMBER OF UNNAMED PASSENGERS',
      'pa_unnamed_si': 'SUM INSURED FOR UNNAMED PA',
      // Add-on Covers
      'dep_reimburse': 'ZERO DEPRECIATION COVER',
      'dep_reimb': 'DEPRECIATION REIMBURSEMENT',
      'return_invoice': 'RETURN TO INVOICE (RTI)',
      'rtn_invoice': 'RETURN TO INVOICE (RTI)',
      'consumbale_expense': 'CONSUMABLE EXPENSES',
      'add_towing': 'ADDITIONAL TOWING CHARGES',
      'add_towing_amount': 'TOWING AMOUNT',
      'rsa': 'ROAD SIDE ASSISTANCE (RSA)',
      'emg_med_exp': 'EMERGENCY MEDICAL EXPENSES',
      'emg_med_exp_si': 'EMERGENCY MEDICAL EXPENSES SI',
      // Legal Liability Covers
      'll_paid': 'LEGAL LIABILITY TO PAID DRIVERS',
      'll_paid_no': 'NUMBER OF PAID DRIVERS (LL)',
      'll_emp': 'LEGAL LIABILITY TO EMPLOYEES (IMT 29)',
      'll_emp_no': 'NUMBER OF EMPLOYEES (LL)',
      // Additional Covers
      'add_tppd': 'ADDITIONAL TPPD',
      'add_tppd_si': 'ADDITIONAL TPPD SUM INSURED',
      'add_pa_owner': 'ADDITIONAL PA COVER TO OWNER-DRIVER',
      'add_pa_owner_si': 'ADDITIONAL PA OWNER SI',
      'add_pa_unnamed': 'ADDITIONAL PA COVER TO UNNAMED PASSENGER',
      'add_pa_unnamed_si': 'ADDITIONAL PA UNNAMED SI',
      // Discount Covers
      'antitheft_cover': 'ANTI-THEFT DISCOUNT',
      'vehicle_blind': 'BLIND/HANDICAP DISCOUNT',
      'own_premises': 'LIMITED TO OWN PREMISE',
      'tppd_discount': 'TPPD DISCOUNT',
      'voluntary_deductibles': 'VOLUNTARY DEDUCTIBLES',
      'voluntary_amount': 'VOLUNTARY DEDUCTIBLE AMOUNT',
      // Loading Covers
      'driving_tution': 'DRIVING TUITION',
      'vehicle_trails_racing': 'VEHICLE ENGAGED IN RACING/RALLIES',
      'ext_racing': 'EXTENSION FOR RACING',
      'imported_veh_without_cus_duty': 'IMPORTED VEHICLE WITHOUT CUSTOM DUTY',
      'fibre_fuel_tank': 'FIBRE FUEL TANK',
      'loss_accessories': 'LOSS OF ACCESSORIES',
      'loss_accessories_idv': 'LOSS OF ACCESSORIES IDV',
      'geography_extension': 'GEOGRAPHICAL AREA EXTENSION',
      // Accessories
      'electrical_acc': 'ELECTRICAL ACCESSORIES',
      'electrical_si': 'ELECTRICAL ACCESSORIES AMOUNT',
      'electrical_des': 'ELECTRICAL ACCESSORIES DESCRIPTION',
      'non_electrical_acc': 'NON-ELECTRICAL ACCESSORIES',
      'non_electrical_si': 'NON-ELECTRICAL ACCESSORIES AMOUNT',
      'non_electrical_des': 'NON-ELECTRICAL ACCESSORIES DESCRIPTION',
      'side_car': 'SIDE CAR',
      'side_car_idv': 'SIDE CAR IDV',
      'cng_lpg_cover': 'CNG/LPG COVER',
      'cng_lpg_si': 'CNG/LPG SUM INSURED',
      // Other
      'automobile_association_cover': 'AUTOMOBILE ASSOCIATION COVER'
    };

    // If custom label exists, return it
    if (labelMap[addonName]) {
      return labelMap[addonName];
    }

    // Otherwise, convert underscores to spaces and capitalize
    return addonName.replace(/_/g, ' ').toUpperCase();
  };

  getSelectOptions = (addonName) => {
    switch (addonName) {
      case 'pa_owner_tenure':
        return this.getPAOwnerTenureOptions();
      default:
        return [
          { value: 'option1', label: 'Option 1' },
          { value: 'option2', label: 'Option 2' }
        ];
    }
  };

  renderAddonInput = (addon) => {
    const addonKey = addon.addons || addon.addon_name || addon.name;
    const addonType = addon.addons_type || addon.addon_type || addon.type;
    const currentValue = this.state.addon_values[addonKey];

    if (!addonKey || !addonType) {
      return null;
    }

    // Handle PA Owner (Compulsory Personal Accident)
    if (addonKey === 'pa_owner') {
      if (!this.shouldShowPAOwner()) {
        return null;
      }

      return (
        <Row key={addonKey}>
          <Col >
            <Form.Check
              type="checkbox"
              id={addonKey}
              label="Do you want to opt for PA Cover?"
              checked={currentValue || false}
              onChange={(e) => this.handleAddonValueChange(addonKey, e.target.checked, 'checkbox')}
            />

          </Col>
        </Row>
      );
    }

    // Handle PA Owner Tenure
    if (addonKey === 'pa_owner_tenure') {
      const paOwnerSelected = this.state.addon_values.pa_owner;

      if (!paOwnerSelected || !this.shouldShowPAOwner()) {
        return null;
      }



      return (
        <Row key={addonKey}>
          <Col >
            <Form.Group className="mb-3">
              <Form.Label>PA Cover Tenure *</Form.Label>
              <Form.Select
                value={currentValue || ''}
                onChange={(e) => this.handleAddonValueChange(addonKey, e.target.value, 'select')}
              >
                <option value="">Select Tenure</option>
                {this.getPAOwnerTenureOptions().map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Required when Compulsory Personal Accident is selected
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      );
    }

    // Handle PA Owner Declaration
    if (addonKey === 'pa_owner_declaration') {
      const paOwnerSelected = this.state.addon_values.pa_owner;

      if (paOwnerSelected) {
        return (
          <Row key={addonKey}>
            <Col >
              <Form.Group className="mb-3">
                <Form.Label>Declaration</Form.Label>
                <Form.Select
                  value={currentValue || ''}
                  onChange={(e) => this.handleAddonValueChange(addonKey, e.target.value, 'select')}
                >
                  <option value="">Select Declaration</option>
                  <option value="None">None</option>

                  {this.getPAOwnerDeclarationOptions().map(option => (

                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        );
      } else {
        const declarationOptions = this.getPAOwnerDeclarationOptions();

        return (
          <Row key={addonKey}>
            <Col className="d-flex">
              <Form.Group className="mb-3">
                <Form.Label>Owner Driver Declaration *</Form.Label>
                <Form.Select
                  value={currentValue || 'Owner driver does not hold valid Driving license'}
                  onChange={(e) => this.handleAddonValueChange(addonKey, e.target.value, 'select')}
                  required
                >
                  <option value="None">None</option>
                  {declarationOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Mandatory field - select appropriate declaration
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        );
      }
    }

    // Default cases for other addon types
    switch (addonType) {
      case 'checkbox':
        return (
          <Form.Check
            key={addonKey}
            type="checkbox"
            id={addonKey}
            label={this.formatAddonLabel(addonKey)}
            checked={currentValue || false}
            onChange={(e) => this.handleAddonValueChange(addonKey, e.target.checked, 'checkbox')}
          />
        );

      case 'select':
        return (
          <Form.Group key={addonKey} className="mb-3">
            <Form.Label>{this.formatAddonLabel(addonKey)}</Form.Label>
            <Form.Select
              value={currentValue || ''}
              onChange={(e) => this.handleAddonValueChange(addonKey, e.target.value, 'select')}
            >
              <option value="">Select {this.formatAddonLabel(addonKey)}</option>
              {this.getSelectOptions(addonKey).map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        );

      case 'textbox':
        return (
          <Form.Group key={addonKey} className="mb-3">
            <Form.Label>{this.formatAddonLabel(addonKey)}</Form.Label>
            <Form.Control
              type="text"
              value={currentValue || ''}
              onChange={(e) => this.handleAddonValueChange(addonKey, e.target.value, 'textbox')}
              placeholder={`Enter ${this.formatAddonLabel(addonKey)}`}
            />
          </Form.Group>
        );
      case 'date':
        return (
          <Form.Group key={addonKey} className="mb-3">
            <Form.Label>{this.formatAddonLabel(addonKey)}</Form.Label>
            <DatePicker
              selected={currentValue ? new Date(currentValue) : null}
              onChange={(date) =>
                this.handleAddonValueChange(
                  addonKey,
                  date ? date.toISOString().split('T')[0] : '',
                  'date'
                )
              }
              dateFormat="yyyy-MM-dd"
              className="form-control"
              placeholderText={`Select ${this.formatAddonLabel(addonKey)}`}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />
          </Form.Group>
        );

      default:
        return null;
    }
  };


  handleSubmit = (e) => {
    e.preventDefault();

    if (!this.state.regno_1 || !this.state.regno_2 || !this.state.regno_3 || !this.state.regno_4) {
      this.showToast("Please enter complete registration number", "error");
      return;
    }

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("rCode1", this.state.regno_1);
    formData.append("rCode2", this.state.regno_2);

    fetch(configData.api_url + "C_Tw_rto_master/getRtoLocation", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({
            place_reg_no: data.list.txt_rtolocation_code,
            place_reg: data.list.txt_rtolocation_name,
            proposer_pincode: data.list.num_pincode,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching RTO location:", error);
        this.showToast("Error fetching location data", "error");
      });

    this.setState({ showSecondAccordion: true, activeKey: "1" });
  };

  handlePreviousStep = () => {
    this.setState({ activeKey: "0" });
  };

  MototquoterequestsubmitHandler = (e) => {
    e.preventDefault();

    // Run all validations including Excel requirements
    const vehicleValidation = this.validateVehicleDetails();
    if (!vehicleValidation.isValid) {
      this.showToast(vehicleValidation.message, "error");
      return;
    }

    const addonValidation = this.validateRequiredAddons();
    if (!addonValidation.isValid) {
      this.showToast(addonValidation.message, "error");
      return;
    }

    const policyDateValidation = this.validateSATPPolicyStartDate();
    if (!policyDateValidation.isValid) {
      this.showToast(policyDateValidation.message, "error");
      return;
    }

    // Excel Requirements Validation
    const excelValidation = this.validateExcelRequirements();
    if (!excelValidation.isValid) {
      excelValidation.errors.forEach(error => {
        this.showToast(error, "error");
      });
      return;
    }
    const nstpCheck = this.checkNSTPCondition();
    if (nstpCheck.isNSTP) {
      this.showToast(
        `🚫 Quote Blocked: NSTP Detected — ${nstpCheck.reason}. Inspection/Manual Approval Required.`,
        "error"
      );
      return; //⬅ BLOCK API call
    }
    const policyPlanDetails = this.getPolicyPlanDetails(this.state.plan_type);
    console.log("Plan Type:", this.state.plan_type);
    console.log("Policy Plan Details:", policyPlanDetails);

    const boolToString = (value) => value ? "true" : "false";
    const getAddonValue = (addonKey, fallback = "") => {
      return this.state.addon_values[addonKey] !== undefined
        ? this.state.addon_values[addonKey]
        : fallback;
    };

    const paOwnerSelected = getAddonValue('pa_owner', false);
    const shouldShowPA = this.shouldShowPAOwner();

    // ✅ CRITICAL FIX: Use correct office location and SOL ID
    const jsonData = {
      quote_id: "",
      idv: "",
      vehicle_idv: this.state.vehicle_idv || "",
      q_producer_code: "4984727878",
      q_producer_email: "krutika.qualitykiosk@tataaig.com",
      is_posp: "N",
      // q_office_location: "90300",  // ✅ FIXED: Changed from 90431 to 90300
      // sol_id: "55552",              // ✅ FIXED: Changed from 55554 to 55552
      q_agent_pan: "",
      pol_tenure: policyPlanDetails.tenure,
      business_type: this.state.business_type,
      proposer_type: this.state.proposer_type,
      plan_type: this.state.plan_type,
      fleet_policy: "false",
      fleet_code: "",
      fleet_name: "",
      pol_start_date: this.state.pol_start_date,

      // PA Owner Logic (only for Individual proposer type)
      ...(shouldShowPA ? {
        pa_owner: boolToString(paOwnerSelected),
        pa_owner_tenure: paOwnerSelected ? getAddonValue('pa_owner_tenure', "") : "",
        pa_owner_declaration: paOwnerSelected ? "None" : getAddonValue('pa_owner_declaration', "Owner driver does not hold valid Driving license")
      } : {
        pa_owner: "false",
        pa_owner_tenure: "",
        pa_owner_declaration: "None"
      }),

      // CPA dates (from state, not addon_values)
      cpa_start_date: this.state.cpa_start_date || "",
      cpa_end_date: this.state.cpa_end_date || "",
      driver_age: "",
      driver_gender: "",
      driver_occupation: "",
      claim_last: "false",
      claim_last_count: "",
      claim_last_amount: "",
      pre_pol_ncb: this.state.pre_pol_ncb,
      no_past_pol: "false",
      prev_pol_start_date: this.state.prev_pol_start_date || "",
      prev_pol_end_date: this.state.prev_pol_end_date || "",
      prev_pol_type: this.state.prev_policy_type ? this.state.prev_policy_type.toLowerCase() : "",
      rtn_invoice: boolToString(getAddonValue('rtn_invoice', false)),
      cng_lpg: this.state.cng_kit_selected ? "true" : "false",
      dep_reimb: boolToString(getAddonValue('dep_reimburse', false)),
      ble_tp_start: this.state.bletpstart || "",
      ble_tp_end: this.state.bletpend || "",
      ble_od_start: this.state.bleodstart || "",
      ble_od_end: this.state.bleodend || "",
      special_regno: "false",
      regno_1: this.state.regno_1,
      regno_2: this.state.regno_2,
      regno_3: this.state.regno_3,
      regno_4: this.state.regno_4,
      place_reg: this.state.place_reg,
      place_reg_no: this.state.place_reg_no,
      proposer_pincode: this.state.proposer_pincode,

      vehicle_make: this.state.vehicle_make?.label || this.state.vehicle_make || "",
      vehicle_model: this.state.vehicle_model?.label || this.state.vehicle_model || "",
      vehicle_variant: this.state.vehicle_variant?.label || this.state.variant || "",
      dor: this.state.dor || "",
      man_year: this.state.man_year || "",
      manu_month: this.state.manu_month || "",
      make_code: this.state.make_code,
      model_code: this.state.model_code,
      variant_code: this.state.variant_code,
      veh_plying_city: "",  // ✅ Moved to correct position after variant_code
      side_car: "false",
      side_car_idv: "",

      // Accessories
      non_electrical_acc: boolToString(getAddonValue('non_electrical_acc', false)),
      non_electrical_si: getAddonValue('non_electrical_si', ""),
      non_electrical_des: getAddonValue('non_electrical_des', ""),
      electrical_acc: boolToString(getAddonValue('electrical_acc', false)),
      electrical_si: getAddonValue('electrical_si', ""),
      electrical_des: getAddonValue('electrical_des', ""),

      cng_lpg_cover: boolToString(getAddonValue('cng_lpg_cover', false)),
      cng_lpg_si: getAddonValue('cng_lpg_si', ""),
      automobile_association_cover: boolToString(getAddonValue('automobile_association_cover', false)),
      automobile_association_mem_exp_date: getAddonValue('automobile_association_mem_exp_date', ""),
      automobile_association_mem_no: getAddonValue('automobile_association_mem_no', ""),

      // Discount Covers - Issue #4 Fix
      antitheft_cover: boolToString(getAddonValue('antitheft_cover', false)),
      vehicle_blind: boolToString(getAddonValue('vehicle_blind', false)),
      own_premises: boolToString(getAddonValue('own_premises', false)),
      tppd_discount: boolToString(getAddonValue('tppd_discount', false)),
      voluntary_deductibles: boolToString(getAddonValue('voluntary_deductibles', false)),
      voluntary_amount: getAddonValue('voluntary_amount', ""),
      voluntary_deductibles_amt: getAddonValue('voluntary_amount', ""),
      uw_discount: getAddonValue('uw_discount', ""),
      uw_loading: getAddonValue('uw_loading', ""),

      // PA Paid/Unnamed - Issue #2 Fix (Previously hardcoded to false)
      pa_paid: boolToString(getAddonValue('pa_paid', false)),
      pa_paid_no: getAddonValue('pa_paid_no', ""),
      pa_paid_si: getAddonValue('pa_paid_si', ""),
      pa_unnamed: boolToString(getAddonValue('pa_unnamed', false)),
      pa_unnamed_no: getAddonValue('pa_unnamed_no', ""),
      pa_unnamed_si: getAddonValue('pa_unnamed_si', ""),

      // Depreciation & RTI
      dep_reimburse: boolToString(getAddonValue('dep_reimburse', false)),
      dep_reimburse_claims: getAddonValue('dep_reimburse_claims', ""),
      dep_reimburse_deductible: getAddonValue('dep_reimburse_deductible', ""),
      return_invoice: boolToString(getAddonValue('return_invoice', false)),

      // Additional Covers - Issue #4 Fix (Previously hardcoded to false)
      consumbale_expense: boolToString(getAddonValue('consumbale_expense', false)),
      add_towing: boolToString(getAddonValue('add_towing', false)),
      add_towing_amount: getAddonValue('add_towing_amount', ""),
      rsa: boolToString(getAddonValue('rsa', false)),
      emg_med_exp: boolToString(getAddonValue('emg_med_exp', false)),
      emg_med_exp_si: getAddonValue('emg_med_exp_si', ""),

      // Legal Liability Covers - Issue #4 Fix (Previously hardcoded to false)
      ll_paid: boolToString(getAddonValue('ll_paid', false)),
      ll_paid_no: getAddonValue('ll_paid_no', ""),
      ll_emp: boolToString(getAddonValue('ll_emp', false)),
      ll_emp_no: getAddonValue('ll_emp_no', ""),

      // Additional TPPD & PA Covers
      add_tppd: boolToString(getAddonValue('add_tppd', false)),
      add_tppd_si: getAddonValue('add_tppd_si', ""),
      add_pa_owner: boolToString(getAddonValue('add_pa_owner', false)),
      add_pa_owner_si: getAddonValue('add_pa_owner_si', ""),
      add_pa_unnamed: boolToString(getAddonValue('add_pa_unnamed', false)),
      add_pa_unnamed_si: getAddonValue('add_pa_unnamed_si', ""),

      // Loading Covers
      driving_tution: boolToString(getAddonValue('driving_tution', false)),
      vehicle_trails_racing: boolToString(getAddonValue('vehicle_trails_racing', false)),
      event_name: getAddonValue('event_name', ""),
      promoter_name: getAddonValue('promoter_name', ""),
      event_from_date: getAddonValue('event_from_date', ""),
      event_to_date: getAddonValue('event_to_date', ""),
      ext_racing: boolToString(getAddonValue('ext_racing', false)),
      imported_veh_without_cus_duty: boolToString(getAddonValue('imported_veh_without_cus_duty', false)),
      fibre_fuel_tank: boolToString(getAddonValue('fibre_fuel_tank', false)),
      loss_accessories: boolToString(getAddonValue('loss_accessories', false)),
      loss_accessories_idv: getAddonValue('loss_accessories_idv', ""),
      geography_extension: boolToString(getAddonValue('geography_extension', false)),
      geography_extension_bang: boolToString(getAddonValue('geography_extension_bang', false)),
      geography_extension_bhutan: boolToString(getAddonValue('geography_extension_bhutan', false)),
      geography_extension_lanka: boolToString(getAddonValue('geography_extension_lanka', false)),
      geography_extension_maldives: boolToString(getAddonValue('geography_extension_maldives', false)),
      geography_extension_nepal: boolToString(getAddonValue('geography_extension_nepal', false)),
      geography_extension_pak: boolToString(getAddonValue('geography_extension_pak', false)),

      __finalize: "1",
    };

    // ✅ REMOVED: Do NOT add these fields as they're not in the working JSON:
    // - policy_plan_id
    // - is_satp
    // - current_ncb_calculated
    // - future_expiry_date (unless specifically needed)
    // - prev_insurer
    // - prev_policy_had_zero_dep
    // - puc_expiry_date
    // - financier_name
    // - financier_branch
    // - terms_accepted
    // - breaking_period_days
    // - cng_annual_premium

    console.log("Complete JSON Data:", JSON.stringify(jsonData, null, 2));

    const jsonData1 = JSON.stringify(jsonData);
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("integId", 327);
    formData.append("savedata", "yes");
    formData.append("jsonData", jsonData1);

    fetch(configData.api_url + "C_Integrationlibrary/saveJson", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("API Response:", data);
        this.showToast(data.message, data.status);

        if (data.status === "success") {
          sessionStorage.setItem("requestId", data.requestId);
          sessionStorage.setItem("final_addon_values", JSON.stringify(this.state.addon_values));
          this.storeVehicleDetailsToSessionStorage();
          sessionStorage.setItem("motorQuoteRequest", jsonData1);
          setTimeout(() => {
            window.location.href = "/mquote";
          }, 2000);
        }
      })
      .catch((error) => {
        console.error("Submit error:", error);
        this.showToast("An error occurred while submitting. Please try again.", "error");
      });
  };
  storeVehicleDetailsToSessionStorage = () => {
    const fieldsToStore = {
      business_type: this.state.business_type,
      proposer_type: this.state.proposer_type,
      plan_type: this.state.plan_type,
      pol_start_date: this.state.pol_start_date,
      prev_pol_start_date: this.state.prev_pol_start_date,
      prev_pol_end_date: this.state.prev_pol_end_date,
      bletpstart: this.state.bletpstart,
      bletpend: this.state.bletpend,
      bleodstart: this.state.bleodstart,
      bleodend: this.state.bleodend,
      regno_1: this.state.regno_1,
      regno_2: this.state.regno_2,
      regno_3: this.state.regno_3,
      regno_4: this.state.regno_4,
      // Excel requirement fields
      prev_insurer: this.state.prev_insurer,
      prev_policy_had_zero_dep: this.state.prev_policy_had_zero_dep,
      // puc_expiry_date: this.state.puc_expiry_date,
      financier_name: this.state.financier_name,
      financier_branch: this.state.financier_branch,
      terms_accepted: this.state.terms_accepted,
      // CPA dates
      cpa_start_date: this.state.cpa_start_date,
      cpa_end_date: this.state.cpa_end_date
    };

    Object.keys(fieldsToStore).forEach(key => {
      if (fieldsToStore[key]) {
        sessionStorage.setItem(key, fieldsToStore[key]);
      }
    });

    if (Object.keys(this.state.addon_values).length > 0) {
      sessionStorage.setItem('addon_values', JSON.stringify(this.state.addon_values));
    }

    if (this.state.filtered_addons.length > 0) {
      sessionStorage.setItem('filtered_addons', JSON.stringify(this.state.filtered_addons));
    }
  };


  checkNSTPCondition = () => {
    const {
      prev_policy_type,
      addon_values = {},
      prev_pol_end_date,
      business_type,
      plan_type,
      prev_policy_had_zero_dep,
      prev_policy_had_rti,
      prev_policy_had_cng_lpg,
      hasPreviousPolicy
    } = this.state;

    const today = new Date();

    // Rule 1: Break-in (with grace period logic)
    if (prev_pol_end_date) {
      const prevEndDate = new Date(prev_pol_end_date);
      const daysDifference = Math.ceil((today - prevEndDate) / (1000 * 60 * 60 * 24));

      // Roll Over: > 90 days is NSTP
      if (business_type === "Roll Over" && daysDifference > 90) {
        return { isNSTP: true, reason: `Break-in: ${daysDifference} days gap exceeds 90-day grace period for Roll Over` };
      }

      // Breaking: > 365 days is NSTP
      if (business_type === "Breaking" && daysDifference > 365) {
        return { isNSTP: true, reason: `Break-in: ${daysDifference} days gap exceeds 365-day limit for Breaking` };
      }
    }

    // Rule 2: Add-on upgrade (only NEW add-ons not in previous policy)
    const addonUpgrade =
      (addon_values.dep_reimburse && !prev_policy_had_zero_dep) ||
      (addon_values.rtn_invoice && !prev_policy_had_rti) ||
      (addon_values.cng_lpg_cover && !prev_policy_had_cng_lpg);

    if (addonUpgrade && prev_policy_type) {
      return { isNSTP: true, reason: "Upgraded to new add-ons not present in previous policy (Zero Dep/RTI/CNG-LPG)" };
    }

    // Rule 3: TP → Package
    if (prev_policy_type === "TP" && plan_type && plan_type.includes("Package")) {
      return { isNSTP: true, reason: "Previous policy TP → Current Package (OD coverage added)" };
    }

    // Rule 4: Used Vehicle / No past policy details
    if (!hasPreviousPolicy || business_type === "Used Vehicle") {
      return { isNSTP: true, reason: "Used vehicle with no past insurance history" };
    }

    return { isNSTP: false };
  };





  render() {
    const {
      plan_type,
      showSecondAccordion,
      pol_end_date,
      tp_end_date,
      business_type,
      vehicle_age_display,
      precise_vehicle_age,
      isLoading,
      current_ncb_calculated,
      breaking_period_days
    } = this.state;

    const eligiblePlans = this.getEligiblePlans();
    const preciseAgeDisplay = this.formatPreciseVehicleAge(precise_vehicle_age);

    return (
      <div className="bgalphacolor">
        <h3 className="logoskybluecolor">Motor Quotes Page</h3>

        <Row className="mb-3 SATPMotorQuotesPage">
          <Col className="col-md-3 active-page">
            <div className="widget_BG widget_BG_grid fill nav-icon">
              <i className="bi bi-car-front-fill"></i>
              <p className="nav-label mt-2">Vehicle Details</p>
            </div>
          </Col>
          <Col className="col-md-3 Inactive">
            <div className="widget_BG widget_BG_grid nav-icon">
              <i className="bi bi-file-richtext "></i>
              <p className="nav-label mt-2">Quotes</p>
            </div>
          </Col>
          <Col className="col-md-3 Inactive">
            <div className="widget_BG widget_BG_grid nav-icon">
              <i className="bi bi-info-square"></i>
              <p className="nav-label mt-2">Vehicle Info</p>
            </div>
          </Col>
          <Col className="col-md-3 Inactive">
            <div className="widget_BG widget_BG_grid nav-icon">
              <i className="bi bi-file-person"></i>
              <p className="nav-label mt-2">Customer Details</p>
            </div>
          </Col>
        </Row>

        <Row className="SATPMotorQuotesPageMain">
          <Col lg="12" md="12">
            <Accordion activeKey={this.state.activeKey} className="m-0 p-0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Enhanced SATP Vehicle Registration Details</Accordion.Header>
                <Accordion.Body>
                  <Form onSubmit={this.handleSubmit}>
                    <Row>
                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Vehicle Registration No</Form.Label>
                          <div style={{ display: "flex", gap: "5px" }}>
                            <Form.Control
                              type="text"
                              maxLength="2"
                              value={this.state.regno_1}
                              onChange={this.handleRegNo1Change}
                              placeholder="MH"
                            />
                            <Form.Control
                              type="text"
                              maxLength="2"
                              value={this.state.regno_2}
                              onChange={this.handleRegNo2Change}
                              placeholder="01"
                            />
                            <Form.Control
                              type="text"
                              maxLength="2"
                              value={this.state.regno_3}
                              onChange={this.handleRegNo3Change}
                              placeholder="CK"
                            />
                            <Form.Control
                              type="text"
                              maxLength="4"
                              value={this.state.regno_4}
                              onChange={this.handleRegNo4Change}
                              placeholder="1776"
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Select Business Type:</Form.Label>
                          <Form.Select
                            name="business_type"
                            value={this.state.business_type}
                            onChange={this.handlebusiness_typeChange}
                          >
                            <option value="">-- Select --</option>
                            <option value="Roll Over">Roll Over</option>

                            {/* <option value="New Business">New Business</option> */}
                            {/* <option value="Breaking">Breaking</option> */}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Select Proposer Type:</Form.Label>
                          <Form.Select
                            name="proposer_type"
                            value={this.state.proposer_type}
                            onChange={this.handleproposer_typeChange}
                          >
                            <option value="">-- Select --</option>
                            <option value="Individual">Individual</option>
                            {/* <option value="Organization">Organization</option> */}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col className="mb-2">
                        <Form.Group>
                          <Form.Label>Date of Registration:</Form.Label>
                          <div className="custom-calendar">
                            <DatePicker
                              selected={this.state.dor ? new Date(this.state.dor) : null}
                              onChange={this.handledordatesChange}
                              className="myDatePicker"
                              showMonthDropdown
                              showYearDropdown
                              dateFormat="dd/MM/yyyy"
                              placeholderText="DD/MM/YYYY"
                              maxDate={new Date()}
                            />
                          </div>
                        </Form.Group>
                      </Col>

                      {/* Enhanced Precise Vehicle Age Display */}
                      {preciseAgeDisplay !== "0Y 0M 0D" && (
                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Group>
                            <Form.Label>Precise Vehicle Age:</Form.Label>
                            <Form.Control
                              type="text"
                              value={preciseAgeDisplay}
                              disabled
                              style={{
                                backgroundColor: eligiblePlans.length > 0 ? "#e8f5e8" : "#ffebee",
                                color: eligiblePlans.length > 0 ? "#2d5a2d" : "#c62828",
                                fontWeight: "bold"
                              }}
                            />
                            <Form.Text className={eligiblePlans.length > 0 ? "text-success" : "text-danger"}>
                              {eligiblePlans.length > 0
                                ? `Eligible for ${eligiblePlans.length} plans`
                                : "No eligible plans available"
                              }
                            </Form.Text>
                            <Form.Text className="text-info d-block">
                              {plan_type && plan_type.toLowerCase().includes('package') &&
                                "Max allowed: 15Y 8M 25D"
                              }
                              {plan_type && plan_type.toLowerCase().includes('standalone tp') &&
                                "Max allowed: 20Y 8M 23D"
                              }
                              {plan_type && plan_type.toLowerCase().includes('standalone od') &&
                                "Max allowed: 15Y 8M 25D"
                              }
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      )}

                      {this.state.business_type === "Roll Over" && (
                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Group>
                            <Form.Label>Previous Policy NCB %:</Form.Label>
                            <Form.Select
                              name="pre_pol_ncb"
                              value={this.state.pre_pol_ncb}
                              onChange={this.handleNCBChange}
                            >
                              <option value="0">0%</option>
                              <option value="10">10%</option>
                              <option value="20">20%</option>
                              <option value="25">25%</option>
                              <option value="35">35%</option>
                              <option value="45">45%</option>
                              <option value="50">50%</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}

                      {/* Auto-calculated NCB Display */}
                      {this.state.business_type === "Roll Over" && current_ncb_calculated !== "0" && (
                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Group>
                            <Form.Label>Current NCB % (Auto-calculated):</Form.Label>
                            <Form.Control
                              type="text"
                              value={`${current_ncb_calculated}%`}
                              disabled
                              style={{ backgroundColor: "#e8f5e8", fontWeight: "bold" }}
                            />
                            <Form.Text className="text-success">
                              Auto-incremented as per Excel requirements
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      )}

                      <Col md="3" sm="6" lg="4">
                        <Form.Group>
                          <Form.Label>Plans Type:</Form.Label>
                          <Form.Select
                            name="plan_type"
                            value={this.state.plan_type}
                            onChange={this.handleplanChange}
                            className="form-control"
                            disabled={!this.state.business_type || !this.state.dor || isLoading}
                          >
                            <option value="">Select Plan Type</option>
                            {isLoading ? (
                              <option disabled>Loading plans...</option>
                            ) : eligiblePlans.length === 0 && this.state.dor && this.state.business_type ? (
                              <option disabled>
                                No eligible plans for {preciseAgeDisplay} old vehicle with {this.state.business_type}
                              </option>
                            ) : (
                              eligiblePlans.map((product, index) => {
                                const planName = product.productName || product.planName || product.name;
                                return (
                                  <option key={index} value={planName}>
                                    {planName}
                                  </option>
                                );
                              })
                            )}
                          </Form.Select>
                          {!this.state.business_type && (
                            <Form.Text className="text-muted">
                              Please select business type first
                            </Form.Text>
                          )}
                          {this.state.business_type && !this.state.dor && (
                            <Form.Text className="text-muted">
                              Please select registration date first to see eligible plans
                            </Form.Text>
                          )}
                          {this.state.business_type && this.state.dor && (
                            <Form.Text className="text-info">
                              Showing {eligiblePlans.length} plans eligible for {preciseAgeDisplay} old vehicle
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      <Col className="mt-4 mb-2">
                        <Form.Group>
                          <Form.Check
                            type="checkbox"
                            label={`CNG Kit (Additional ₹${this.state.cng_annual_charge} per year in TP section)`}
                            checked={this.state.cng_kit_selected}
                            onChange={(e) => this.setState({
                              cng_kit_selected: e.target.checked,
                              cng_lpg: e.target.checked
                            })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Enhanced Vehicle Age Eligibility Summary */}
                    {this.state.dor && this.state.business_type && (
                      <Row className="mt-3">
                        <Col>
                          <Alert variant={eligiblePlans.length > 0 ? "info" : "warning"}>
                            <strong>Enhanced Vehicle Eligibility Summary :</strong>
                            <br />
                            <strong>Vehicle Age:</strong> {preciseAgeDisplay} ({this.state.precise_vehicle_age.totalInYears} years)
                            <br />
                            <strong>Business Type:</strong> {this.state.business_type}
                            <br />
                            <strong>Eligible Plans:</strong> {eligiblePlans.length} available
                            {eligiblePlans.length > 0 ? (
                              <ul className="mb-0 mt-2">
                                {eligiblePlans.map((plan, index) => {
                                  const planName = plan.productName || plan.planName || plan.name;
                                  let maxAgeText = "";
                                  if (planName.toLowerCase().includes('package')) {
                                    maxAgeText = "Max age: 15Y 8M 25D";
                                  } else if (planName.toLowerCase().includes('standalone tp')) {
                                    maxAgeText = "Max age: 20Y 8M 23D";
                                  } else if (planName.toLowerCase().includes('standalone od')) {
                                    maxAgeText = "Max age: 15Y 8M 25D";
                                  }
                                  return (
                                    <li key={index}>
                                      {planName}
                                      {maxAgeText && (
                                        <span className="text-muted"> ({maxAgeText})</span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <div className="mt-2">
                                <span className="text-warning">
                                  This vehicle exceeds the precise age limits for the selected business type.
                                  Please verify registration date or consider different business type.
                                </span>
                              </div>
                            )}
                          </Alert>
                        </Col>
                      </Row>
                    )}

                    {/* SAOD CPA Mandatory Alert */}


                    {/* Breaking Period Display for SATP */}
                    {this.state.business_type === "Breaking" && breaking_period_days > 0 && (
                      <Row className="mt-3">
                        <Col lg={12}>
                          <Alert variant={breaking_period_days <= 90 ? "info" : breaking_period_days <= 365 ? "warning" : "danger"}>
                            <strong>SATP Breaking Period Information:</strong>
                            <br />
                            Breaking Period: {breaking_period_days} days
                            <br />
                            {breaking_period_days <= 90 &&
                              "Category: Breaking within 90 days (As per Excel SATP Req #2)"
                            }
                            {breaking_period_days > 90 && breaking_period_days <= 365 &&
                              "Category: Breaking between 90-365 days (As per Excel SATP Req #3)"
                            }
                            {breaking_period_days > 365 &&
                              "Category: Breaking over 365 days (As per Excel SATP Req #4)"
                            }
                          </Alert>
                        </Col>
                      </Row>
                    )}

                    {/* Enhanced Add-ons Section with Excel Requirements */}
                    {this.state.filtered_addons.length > 0 && (
                      <div className="mt-4">
                        <h6 className="text-primary mb-3">ADD-ONS & COVERS </h6>

                        {/* Accessories Section with Excel Limits */}
                        <Row className="mb-3">
                          <Col lg={12}>
                            <h6 className="text-secondary">ACCESSORIES</h6>
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="ELECTRICAL ACCESSORIES"
                              checked={this.state.addon_values.electrical_acc || false}
                              onChange={(e) => this.handleAddonValueChange('electrical_acc', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.electrical_acc && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>ELECTRICAL ACCESSORIES AMOUNT *</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={this.state.addon_values.electrical_si || ""}
                                  onChange={(e) => {
                                    const amount = parseFloat(e.target.value) || 0;
                                    if (this.validateAccessoriesAmount(amount, "Electrical")) {
                                      this.handleAddonValueChange('electrical_si', amount, 'textbox');
                                    } else {
                                      e.target.value = "";
                                    }
                                  }}
                                  placeholder="Enter amount"
                                  max="15000"
                                  required
                                />
                                <Form.Text className="text-danger">
                                  Maximum limit: ₹15,000
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          )}

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="NON-ELECTRICAL ACCESSORIES"
                              checked={this.state.addon_values.non_electrical_acc || false}
                              onChange={(e) => this.handleAddonValueChange('non_electrical_acc', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.non_electrical_acc && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>NON-ELECTRICAL ACCESSORIES AMOUNT *</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={this.state.addon_values.non_electrical_si || ""}
                                  onChange={(e) => {
                                    const amount = parseFloat(e.target.value) || 0;
                                    if (this.validateAccessoriesAmount(amount, "Non-Electrical")) {
                                      this.handleAddonValueChange('non_electrical_si', amount, 'textbox');
                                    } else {
                                      e.target.value = "";
                                    }
                                  }}
                                  placeholder="Enter amount"
                                  max="15000"
                                  required
                                />
                                <Form.Text className="text-danger">
                                  Maximum limit: ₹15,000
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          )}
                        </Row>

                        {/* PA COVERS SECTION - Issue #2 Fix */}
                        <Row className="mb-3">
                          <Col lg={12}>
                            <h6 className="text-secondary">PA COVERS</h6>
                          </Col>

                          {/* PA Paid Drivers */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="PA COVERED PAID DRIVERS"
                              checked={this.state.addon_values.pa_paid || false}
                              onChange={(e) => this.handleAddonValueChange('pa_paid', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.pa_paid && (
                            <>
                              <Col md="3" sm="6" lg="4" className="mb-2">
                                <Form.Group>
                                  <Form.Label>NUMBER OF PAID DRIVERS *</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={this.state.addon_values.pa_paid_no || ""}
                                    onChange={(e) => this.handleAddonValueChange('pa_paid_no', e.target.value, 'textbox')}
                                    placeholder="Enter number"
                                    min="1"
                                    required
                                  />
                                </Form.Group>
                              </Col>
                              <Col md="3" sm="6" lg="4" className="mb-2">
                                <Form.Group>
                                  <Form.Label>SUM INSURED FOR PAID DRIVERS *</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={this.state.addon_values.pa_paid_si || ""}
                                    onChange={(e) => this.handleAddonValueChange('pa_paid_si', e.target.value, 'textbox')}
                                    placeholder="Enter sum insured"
                                    required
                                  />
                                </Form.Group>
                              </Col>
                            </>
                          )}

                          {/* PA Unnamed Passenger - Issue #2 */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="PA UNNAMED PASSENGER"
                              checked={this.state.addon_values.pa_unnamed || false}
                              onChange={(e) => this.handleAddonValueChange('pa_unnamed', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.pa_unnamed && (
                            <>
                              <Col md="3" sm="6" lg="4" className="mb-2">
                                <Form.Group>
                                  <Form.Label>NUMBER OF UNNAMED PASSENGERS *</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={this.state.addon_values.pa_unnamed_no || ""}
                                    onChange={(e) => this.handleAddonValueChange('pa_unnamed_no', e.target.value, 'textbox')}
                                    placeholder="Enter number"
                                    min="1"
                                    required
                                  />
                                </Form.Group>
                              </Col>
                              <Col md="3" sm="6" lg="4" className="mb-2">
                                <Form.Group>
                                  <Form.Label>SUM INSURED FOR UNNAMED PA *</Form.Label>
                                  <Form.Control
                                    type="number"
                                    value={this.state.addon_values.pa_unnamed_si || ""}
                                    onChange={(e) => this.handleAddonValueChange('pa_unnamed_si', e.target.value, 'textbox')}
                                    placeholder="Enter sum insured"
                                    required
                                  />
                                </Form.Group>
                              </Col>
                            </>
                          )}
                        </Row>

                        {/* LEGAL LIABILITY COVERS SECTION */}
                        <Row className="mb-3">
                          <Col lg={12}>
                            <h6 className="text-secondary">LEGAL LIABILITY COVERS</h6>
                          </Col>

                          {/* Legal Liability to Paid Drivers */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="LEGAL LIABILITY TO PAID DRIVERS"
                              checked={this.state.addon_values.ll_paid || false}
                              onChange={(e) => this.handleAddonValueChange('ll_paid', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.ll_paid && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>NUMBER OF PAID DRIVERS (LL) *</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={this.state.addon_values.ll_paid_no || ""}
                                  onChange={(e) => this.handleAddonValueChange('ll_paid_no', e.target.value, 'textbox')}
                                  placeholder="Enter number"
                                  min="1"
                                  required
                                />
                              </Form.Group>
                            </Col>
                          )}

                          {/* Legal Liability to Employees */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="LEGAL LIABILITY TO EMPLOYEES (IMT 29)"
                              checked={this.state.addon_values.ll_emp || false}
                              onChange={(e) => this.handleAddonValueChange('ll_emp', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.ll_emp && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>NUMBER OF EMPLOYEES (LL) *</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={this.state.addon_values.ll_emp_no || ""}
                                  onChange={(e) => this.handleAddonValueChange('ll_emp_no', e.target.value, 'textbox')}
                                  placeholder="Enter number"
                                  min="1"
                                  required
                                />
                              </Form.Group>
                            </Col>
                          )}
                        </Row>

                        {/* ADDITIONAL COVERS SECTION */}
                        <Row className="mb-3">
                          <Col lg={12}>
                            <h6 className="text-secondary">ADDITIONAL COVERS</h6>
                          </Col>

                          {/* Emergency Medical Expenses */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="EMERGENCY MEDICAL EXPENSES"
                              checked={this.state.addon_values.emg_med_exp || false}
                              onChange={(e) => this.handleAddonValueChange('emg_med_exp', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.emg_med_exp && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>EMERGENCY MEDICAL EXPENSES SI *</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={this.state.addon_values.emg_med_exp_si || ""}
                                  onChange={(e) => this.handleAddonValueChange('emg_med_exp_si', e.target.value, 'textbox')}
                                  placeholder="Enter sum insured"
                                  required
                                />
                              </Form.Group>
                            </Col>
                          )}

                          {/* Additional Towing Charges */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="ADDITIONAL TOWING CHARGES"
                              checked={this.state.addon_values.add_towing || false}
                              onChange={(e) => this.handleAddonValueChange('add_towing', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.add_towing && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>TOWING AMOUNT *</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={this.state.addon_values.add_towing_amount || ""}
                                  onChange={(e) => this.handleAddonValueChange('add_towing_amount', e.target.value, 'textbox')}
                                  placeholder="Enter amount"
                                  required
                                />
                              </Form.Group>
                            </Col>
                          )}

                          {/* Additional TPPD */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="ADDITIONAL TPPD"
                              checked={this.state.addon_values.add_tppd || false}
                              onChange={(e) => this.handleAddonValueChange('add_tppd', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.add_tppd && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>ADDITIONAL TPPD SUM INSURED *</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={this.state.addon_values.add_tppd_si || ""}
                                  onChange={(e) => this.handleAddonValueChange('add_tppd_si', e.target.value, 'textbox')}
                                  placeholder="Enter sum insured"
                                  required
                                />
                              </Form.Group>
                            </Col>
                          )}

                          {/* Consumable Expenses */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="CONSUMABLE EXPENSES"
                              checked={this.state.addon_values.consumbale_expense || false}
                              onChange={(e) => this.handleAddonValueChange('consumbale_expense', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {/* Road Side Assistance */}
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="ROAD SIDE ASSISTANCE (RSA)"
                              checked={this.state.addon_values.rsa || false}
                              onChange={(e) => this.handleAddonValueChange('rsa', e.target.checked, 'checkbox')}
                            />
                          </Col>
                        </Row>

                        {/* Zero Depreciation Section */}
                        <Row className="mb-3">
                          <Col lg={12}>
                            <h6 className="text-secondary">ZERO DEPRECIATION COVER</h6>
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="ZERO DEPRECIATION COVER"
                              checked={this.state.addon_values.dep_reimburse || false}
                              onChange={(e) => {
                                this.handleAddonValueChange('dep_reimburse', e.target.checked, 'checkbox');
                                // Reset previous policy zero dep when unchecking
                                if (!e.target.checked) {
                                  this.setState({ prev_policy_had_zero_dep: false });
                                }
                              }}
                            />
                          </Col>

                          {/* Previous Policy had Zero Depreciation - Only shows when Zero Dep is checked */}
                          {this.state.addon_values.dep_reimburse && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Check
                                type="checkbox"
                                label={<span style={{ color: '#dc3545', fontWeight: 'bold' }}>PREVIOUS POLICY HAD ZERO DEPRECIATION *</span>}
                                checked={this.state.prev_policy_had_zero_dep || false}
                                onChange={(e) => this.setState({ prev_policy_had_zero_dep: e.target.checked })}
                                style={{ border: !this.state.prev_policy_had_zero_dep ? '2px solid #dc3545' : 'none', padding: '5px', borderRadius: '5px' }}
                              />
                              {!this.state.prev_policy_had_zero_dep && (
                                <Form.Text className="text-danger">
                                  <strong>* Mandatory:</strong> Please confirm previous policy had Zero Depreciation cover
                                </Form.Text>
                              )}
                            </Col>
                          )}

                          {/* Warning when Zero Dep is selected but previous policy checkbox is not checked */}
                          {this.state.addon_values.dep_reimburse && !this.state.prev_policy_had_zero_dep && (
                            <Col lg={12} className="mb-2">
                              <Alert variant="danger">
                                <strong>⚠️ Required:</strong> To proceed with Zero Depreciation Cover, you must confirm that the previous policy had Zero Depreciation. Quote cannot be generated without this confirmation.
                              </Alert>
                            </Col>
                          )}
                        </Row>

                        {/* Return to Invoice (RTI) Section - Separate from Zero Depreciation */}
                        <Row className="mb-3">
                          <Col lg={12}>
                            <h6 className="text-secondary">RETURN TO INVOICE (RTI)</h6>
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="RETURN TO INVOICE (RTI)"
                              checked={this.state.addon_values.return_invoice || false}
                              onChange={(e) => {
                                this.handleAddonValueChange('return_invoice', e.target.checked, 'checkbox');
                                // Reset previous policy RTI when unchecking
                                if (!e.target.checked) {
                                  this.setState({ prev_policy_had_rti: false });
                                }
                              }}
                            />
                            <Form.Text className="text-muted">
                              Vehicle age must be ≤ 3 years 8 months 28 days
                            </Form.Text>
                          </Col>

                          {/* Previous Policy had RTI - Only shows when RTI is checked */}
                          {this.state.addon_values.return_invoice && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Check
                                type="checkbox"
                                label={<span style={{ color: '#dc3545', fontWeight: 'bold' }}>PREVIOUS POLICY HAD RTI *</span>}
                                checked={this.state.prev_policy_had_rti || false}
                                onChange={(e) => this.setState({ prev_policy_had_rti: e.target.checked })}
                                style={{ border: !this.state.prev_policy_had_rti ? '2px solid #dc3545' : 'none', padding: '5px', borderRadius: '5px' }}
                              />
                              {!this.state.prev_policy_had_rti && (
                                <Form.Text className="text-danger">
                                  <strong>* Mandatory:</strong> Please confirm previous policy had RTI cover
                                </Form.Text>
                              )}
                            </Col>
                          )}

                          {/* Warning when RTI is selected but previous policy checkbox is not checked */}
                          {this.state.addon_values.return_invoice && !this.state.prev_policy_had_rti && (
                            <Col lg={12} className="mb-2">
                              <Alert variant="warning">
                                <strong>⚠️ Required:</strong> To proceed with Return to Invoice (RTI), you must confirm that the previous policy had RTI cover.
                              </Alert>
                            </Col>
                          )}

                          {/* Success message when both Zero Dep and RTI are properly selected */}
                          {this.state.addon_values.dep_reimburse && this.state.addon_values.return_invoice &&
                           this.state.prev_policy_had_zero_dep && this.state.prev_policy_had_rti && (
                            <Col lg={12} className="mb-2">
                              <Alert variant="success">
                                ✅ Zero Depreciation and RTI covers are eligible as previous policies had both covers.
                              </Alert>
                            </Col>
                          )}
                        </Row>

                        {/* DISCOUNT COVERS SECTION */}
                        <Row className="mb-3">
                          <Col lg={12}>
                            <h6 className="text-secondary">DISCOUNT COVERS</h6>
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="ANTI-THEFT DISCOUNT"
                              checked={this.state.addon_values.antitheft_cover || false}
                              onChange={(e) => this.handleAddonValueChange('antitheft_cover', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="BLIND/HANDICAP DISCOUNT"
                              checked={this.state.addon_values.vehicle_blind || false}
                              onChange={(e) => this.handleAddonValueChange('vehicle_blind', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="LIMITED TO OWN PREMISE"
                              checked={this.state.addon_values.own_premises || false}
                              onChange={(e) => this.handleAddonValueChange('own_premises', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="TPPD DISCOUNT"
                              checked={this.state.addon_values.tppd_discount || false}
                              onChange={(e) => this.handleAddonValueChange('tppd_discount', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Check
                              type="checkbox"
                              label="VOLUNTARY DEDUCTIBLES"
                              checked={this.state.addon_values.voluntary_deductibles || false}
                              onChange={(e) => this.handleAddonValueChange('voluntary_deductibles', e.target.checked, 'checkbox')}
                            />
                          </Col>

                          {this.state.addon_values.voluntary_deductibles && (
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>VOLUNTARY DEDUCTIBLE AMOUNT *</Form.Label>
                                <Form.Select
                                  value={this.state.addon_values.voluntary_amount || ""}
                                  onChange={(e) => this.handleAddonValueChange('voluntary_amount', e.target.value, 'select')}
                                  required
                                >
                                  <option value="">Select Amount</option>
                                  <option value="2500">₹2,500</option>
                                  <option value="5000">₹5,000</option>
                                  <option value="7500">₹7,500</option>
                                  <option value="15000">₹15,000</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          )}
                        </Row>

                        {/* Regular Add-ons */}
                        <Row style={{ maxHeight: '500px', overflowY: 'auto' }}>
                          {this.state.filtered_addons.map((addon) => {
                            const addonKey = addon.addons || addon.addon_name || addon.name || addon.motoraddonstable_Id;

                            // Skip accessories as they're handled above
                            if (['electrical_acc', 'electrical_si', 'non_electrical_acc', 'non_electrical_si', 'dep_reimburse', 'return_invoice'].includes(addonKey)) {
                              return null;
                            }

                            return (
                              <Col lg={4} md={6} sm={12} key={addonKey} className="mb-3">
                                {this.renderAddonInput(addon)}
                              </Col>
                            );
                          })}
                        </Row>

                        {/* CPA Date Fields - Show when PA Owner is selected */}
                        {this.shouldShowPAOwner() && this.state.addon_values.pa_owner && (
                          <Row className="mt-4">
                            <Col lg={12}>
                              <h6 className="text-primary mb-3">CPA POLICY DATES (MANDATORY)</h6>
                            </Col>

                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>CPA START DATE *</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.cpa_start_date ? new Date(this.state.cpa_start_date) : null}
                                    onChange={this.handleCPAStartDateChange}
                                    className="myDatePicker"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                    showMonthDropdown
                                    showYearDropdown
                                    minDate={new Date()}
                                  />
                                </div>
                                <Form.Text className="text-info">Required for CPA coverage</Form.Text>
                              </Form.Group>
                            </Col>

                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>CPA END DATE *</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.cpa_end_date ? new Date(this.state.cpa_end_date) : null}
                                    onChange={this.handleCPAEndDateChange}
                                    className="myDatePicker"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                    showMonthDropdown
                                    showYearDropdown
                                    minDate={this.state.cpa_start_date ? new Date(this.state.cpa_start_date) : new Date()}
                                  />
                                </div>
                                <Form.Text className="text-success">
                                  {this.state.addon_values.pa_owner_tenure && this.state.cpa_start_date
                                    ? `Auto-calculated for ${this.state.addon_values.pa_owner_tenure} year(s)`
                                    : "Will auto-calculate on selecting start date"}
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </div>
                    )}

                    {/* Vehicle Selection Section */}
                    <Row className="mt-3">
                      <Col lg={12}>
                        <h6 className="text-primary">VEHICLE SELECTION</h6>
                      </Col>

                      {/* Vehicle Make */}
                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>VEHICLE MAKE</Form.Label>
                          <Select
                            className="rounded-0"
                            options={Object.keys(this.state.apiData).map(make => ({
                              value: make,
                              label: make
                            }))}
                            value={this.state.vehicle_make}
                            onChange={this.handleMakeChange}
                            placeholder="Select Vehicle Make"
                            isSearchable
                          />
                        </Form.Group>
                      </Col>

                      {/* Vehicle Model */}
                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Vehicle Model</Form.Label>
                          <Select
                            options={[...new Set(this.state.models.map(m => m.txt_model_name))].map(
                              model => ({ value: model, label: model })
                            )}
                            value={this.state.vehicle_model}
                            onChange={this.handleModelChange}
                            placeholder="Select Vehicle Model"
                            isDisabled={!this.state.vehicle_make}
                            isSearchable
                          />
                        </Form.Group>
                      </Col>

                      {/* Vehicle Variant */}
                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Vehicle Variant</Form.Label>
                          <Select
                            options={this.state.variants.map(v => ({
                              value: v.txt_model_variant,
                              label: v.txt_model_variant
                            }))}
                            value={this.state.vehicle_variant}
                            onChange={this.handleVariantChange}
                            placeholder="Select Vehicle Variant"
                            isDisabled={!this.state.vehicle_model}
                            isSearchable
                          />
                        </Form.Group>
                      </Col>

                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Make Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="make_code"
                            value={this.state.make_code}
                            disabled
                          />
                        </Form.Group>
                      </Col>

                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Model Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="model_code"
                            value={this.state.model_code}
                            disabled
                          />
                        </Form.Group>
                      </Col>

                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Variant Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="variant_code"
                            value={this.state.variant_code}
                            disabled
                          />
                        </Form.Group>
                      </Col>

                      <Col md="3" sm="6" lg="4" className="mb-2">
                        <Form.Group>
                          <Form.Label>Place of Register</Form.Label>
                          <Form.Control
                            type="text"
                            value={`${this.state.regno_1}${this.state.regno_2}`}
                            disabled
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Terms & Conditions (Excel Requirement #8) */}
                    {/* <Row className="mt-4">
                      <Col lg={12}>
                        <Alert variant="warning">
                          <Form.Check
                            type="checkbox"
                            label="I have read and agree to the Terms & Conditions, Policy Wordings, and Privacy Policy *"
                            name="terms_accepted"
                            checked={this.state.terms_accepted}
                            onChange={(e) => this.setState({ terms_accepted: e.target.checked })}
                            required
                            className="mb-2"
                          />
                          <Form.Text className="text-danger">
                            * Mandatory: Cannot proceed without accepting Terms & Conditions (Excel Requirement #8)
                          </Form.Text>
                        </Alert>
                      </Col>
                    </Row> */}

                    <Row className="justify-content-between mt-3">
                      <Col lg="12" md="12">
                        <Button
                          type="submit"
                          style={{ float: "right" }}
                          className="rounded-0"
                          variant="primary"
                          disabled={isLoading}
                        >
                          {isLoading ? "Loading..." : "Next"}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Accordion.Body>
              </Accordion.Item>

              {/* Second Accordion - Enhanced with Excel Requirements */}
              {showSecondAccordion && (
                <Accordion.Item eventKey="1">
                  <Accordion.Header>Enhanced SATP Policy &  Details</Accordion.Header>
                  <Accordion.Body>
                    <Form>
                      <Row>
                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Label>Registered Place Code</Form.Label>
                          <Form.Control
                            type="text"
                            name="place_reg_no"
                            value={this.state.place_reg_no}
                            onChange={this.handleChange}
                          />
                        </Col>

                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Label>Registered Place</Form.Label>
                          <Form.Control
                            type="text"
                            name="place_reg"
                            value={this.state.place_reg}
                            disabled
                          />
                        </Col>

                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Label>Proposer Pincode</Form.Label>
                          <Form.Control
                            type="text"
                            name="proposer_pincode"
                            value={this.state.proposer_pincode}
                            disabled
                          />
                        </Col>

                        {/* IDV Section (Excel Requirement #2) */}
                        {/* <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Group>
                            <Form.Label>Vehicle IDV </Form.Label>
                            <Form.Control
                              type="number"
                              name="vehicle_idv"
                              value={this.state.vehicle_idv}
                              onChange={(e) => {
                                const idv = parseFloat(e.target.value);
                                this.setState({ vehicle_idv: idv });
                              }}
                              placeholder="Enter IDV amount"
                              min={this.state.min_idv}
                              max={this.state.max_idv}
                            />
                            <Form.Text className="text-info">
                              IDV range validation as per Excel requirements
                            </Form.Text>
                          </Form.Group>
                        </Col> */}

                        <Col className="mb-2">
                          <Form.Group>
                            <Form.Label>Policy Start Date:</Form.Label>
                            <div className="custom-calendar">
                              <DatePicker
                                selected={this.state.pol_start_date ? new Date(this.state.pol_start_date) : null}
                                onChange={this.handlestartdatesChange}
                                className="myDatePicker"
                                showMonthDropdown
                                showYearDropdown
                                dateFormat="dd/MM/yyyy"
                                placeholderText="DD/MM/YYYY"
                                disabled={
                                  // Disabled only for New Business (non-SAOD)
                                  business_type === "New Business" &&
                                  !(plan_type && (plan_type.toLowerCase().includes('standalone od') || plan_type.toLowerCase().includes('saod')))
                                }
                                minDate={
                                  // SAOD - allow today (T+0) for all business types
                                  (plan_type && (plan_type.toLowerCase().includes('standalone od') || plan_type.toLowerCase().includes('saod')))
                                    ? (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })() // Today midnight for SAOD
                                    // SATP - T+1 minimum
                                    : (plan_type && plan_type.toLowerCase().includes('standalone tp'))
                                      ? (() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0); return d; })()
                                      // Roll Over with prev policy - day after prev end date
                                      : (business_type === "Roll Over" && this.state.prev_pol_end_date)
                                        ? (() => { const d = new Date(this.state.prev_pol_end_date); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0); return d; })()
                                        // Default - T+1
                                        : (() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0); return d; })()
                                }
                              />
                              {business_type === "New Business" && !(plan_type && (plan_type.toLowerCase().includes('standalone od') || plan_type.toLowerCase().includes('saod'))) && (
                                <Form.Text className="text-info">
                                  Auto-set to T+1 for New Business
                                </Form.Text>
                              )}
                              {plan_type && (plan_type.toLowerCase().includes('standalone od') || plan_type.toLowerCase().includes('saod')) && (
                                <Form.Text className="text-success">
                                  SAOD Policy: Start date can be today or future date
                                </Form.Text>
                              )}
                              {plan_type && plan_type.toLowerCase().includes('standalone tp') && (
                                <Form.Text className="text-warning">
                                  SATP Policy: Minimum start date is T+1
                                </Form.Text>
                              )}
                              {business_type === "Roll Over" && this.state.prev_pol_end_date && !(plan_type && plan_type.toLowerCase().includes('standalone tp')) && !(plan_type && (plan_type.toLowerCase().includes('standalone od') || plan_type.toLowerCase().includes('saod'))) && (
                                <Form.Text className="text-info">
                                  Select date after previous policy end date
                                </Form.Text>
                              )}
                            </div>
                          </Form.Group>
                        </Col>

                        <Col className="mb-2">
                          <Form.Group>
                            <Form.Label>Future Expiry Date (Optional):</Form.Label>
                            <div className="custom-calendar">
                              <DatePicker
                                selected={this.state.future_expiry_date ? new Date(this.state.future_expiry_date) : null}
                                onChange={this.handleFutureExpiryDateChange}
                                className="myDatePicker"
                                showMonthDropdown
                                showYearDropdown
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Select Future Expiry Date"
                                minDate={new Date()}
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        {/* Display calculated policy dates */}
                        {pol_end_date && (
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Group>
                              <Form.Label>Policy End Date:</Form.Label>
                              <Form.Control
                                type="text"
                                value={pol_end_date}
                                disabled
                                style={{ backgroundColor: "#e8f5e8" }}
                              />
                            </Form.Group>
                          </Col>
                        )}

                        {tp_end_date && (
                          <Col md="3" sm="6" lg="4" className="mb-2">
                            <Form.Group>
                              <Form.Label>TP Coverage End Date:</Form.Label>
                              <Form.Control
                                type="text"
                                value={tp_end_date}
                                disabled
                                style={{ backgroundColor: "#fff8f0" }}
                              />
                            </Form.Group>
                          </Col>
                        )}
                      </Row>

                      <hr />

                      {/* Enhanced Previous Policy Details for Roll Over (Excel Requirements) */}
                      {this.state.business_type === "Roll Over" && (
                        <>
                          <Alert variant="info" className="mt-3">
                            <strong>Roll Over Policy Details</strong> Please provide previous policy information with TATA AIG restriction and NCB auto-increment
                          </Alert>

                          <Row className="">
                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>Previous Insurer *</Form.Label>
                                <Form.Select
                                  name="prev_insurer"
                                  value={this.state.prev_insurer}
                                  onChange={this.handlePreviousInsurerChange}
                                  required
                                >
                                  <option value="">Select Previous Insurer</option>
                                  <option value="HDFC ERGO">HDFC ERGO</option>
                                  <option value="ICICI Lombard">ICICI Lombard</option>
                                  <option value="Bajaj Allianz">Bajaj Allianz</option>
                                  <option value="Reliance General">Reliance General</option>
                                  <option value="New India Assurance">New India Assurance</option>
                                  <option value="Oriental Insurance">Oriental Insurance</option>
                                  <option value="United India">United India</option>
                                  <option value="Other">Other</option>
                                  {/* Note: TATA AIG options removed as per Excel requirement */}
                                </Form.Select>
                                <Form.Text className="text-danger">
                                  Note: TATA AIG cannot be selected as previous insurer
                                </Form.Text>
                              </Form.Group>
                            </Col>

                            <Col md="3" sm="6" lg="4" className="mb-2">
                              <Form.Group>
                                <Form.Label>Previous Policy Type:</Form.Label>
                                <Form.Select
                                  name="prev_policy_type"
                                  value={this.state.prev_policy_type}
                                  onChange={this.handleChange}
                                >
                                  <option value="">-- Select Previous Policy Type --</option>
                                  <option value="Package">Package Policy</option>
                                  <option value="TP" disabled={this.state.isTPDisabled}>
                                    Third Party Only
                                  </option>
                                  <option value="SAOD">Standalone Own Damage</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            {/* Only show "Previous policy had Zero Depreciation" when Zero Depreciation Cover is selected */}
                            {this.state.addon_values.dep_reimburse && (
                              <Col md="3" sm="6" lg="4" className="mb-2">
                                <Form.Group>
                                  <Form.Check
                                    type="checkbox"
                                    label="Previous policy had Zero Depreciation "
                                    name="prev_policy_had_zero_dep"
                                    checked={this.state.prev_policy_had_zero_dep}
                                    onChange={(e) => this.setState({ prev_policy_had_zero_dep: e.target.checked })}
                                  />
                                  <Form.Text className="text-info">
                                    Required if selecting both Zero Dep and RTI
                                  </Form.Text>
                                </Form.Group>
                              </Col>
                            )}

                            <Col className="mb-2">
                              <Form.Group>
                                <Form.Label>Previous Policy Start Date:</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.prev_pol_start_date ? new Date(this.state.prev_pol_start_date) : null}
                                    onChange={this.handledatesChange}
                                    className="myDatePicker"
                                    showMonthDropdown
                                    showYearDropdown
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                  />
                                </div>
                              </Form.Group>
                            </Col>

                            <Col className="mb-2">
                              <Form.Group>
                                <Form.Label>Previous Policy End Date:</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.prev_pol_end_date ? new Date(this.state.prev_pol_end_date) : null}
                                    onChange={this.handledateEndChange}
                                    className="myDatePicker"
                                    showMonthDropdown
                                    showYearDropdown
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                  />
                                </div>
                                <Form.Text className="text-info">
                                  Policy start date will be auto-set to this date + 1
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>

                          <hr />

                          <Row className="">
                            <Col className="mb-2">
                              <Form.Group>
                                <Form.Label>Bundle TP Start Date:</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.bletpstart ? new Date(this.state.bletpstart) : null}
                                    onChange={this.handlebledatesChange}
                                    className="myDatePicker"
                                    showMonthDropdown
                                    showYearDropdown
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                  />
                                </div>
                              </Form.Group>
                            </Col>

                            <Col className="mb-2">
                              <Form.Group>
                                <Form.Label>Bundle TP End Date:</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.bletpend ? new Date(this.state.bletpend) : null}
                                    onChange={this.handleenddatesChange}
                                    className="myDatePicker"
                                    showMonthDropdown
                                    showYearDropdown
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                  />
                                </div>
                              </Form.Group>
                            </Col>

                            <Col className="mb-2">
                              <Form.Group>
                                <Form.Label>Bundle OD Start Date:</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.bleodstart ? new Date(this.state.bleodstart) : null}
                                    onChange={this.handleoddatesChange}
                                    className="myDatePicker"
                                    showMonthDropdown
                                    showYearDropdown
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                  />
                                </div>
                              </Form.Group>
                            </Col>

                            <Col className="mb-2">
                              <Form.Group>
                                <Form.Label>Bundle OD End Date:</Form.Label>
                                <div className="custom-calendar">
                                  <DatePicker
                                    selected={this.state.bleodend ? new Date(this.state.bleodend) : null}
                                    onChange={this.handleodenddatesChange}
                                    className="myDatePicker"
                                    showMonthDropdown
                                    showYearDropdown
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="DD/MM/YYYY"
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* PUC Details Section (Excel Requirement #10) */}
                      {/* <Row className="mt-3">
                        <Col lg={12}>
                          <h6 className="text-primary">PUC Details</h6>
                        </Col>

                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Group>
                            <Form.Check
                              type="checkbox"
                              label="PUC details not available"
                              name="puc_not_provided"
                              checked={this.state.puc_not_provided}
                              onChange={(e) => {
                                this.setState({
                                  puc_not_provided: e.target.checked,
                                  puc_expiry_date: e.target.checked ? "" : this.state.puc_expiry_date
                                });
                              }}
                            />
                            <Form.Text className="text-info">
                              Check this if PUC details are not available
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        {!this.state.puc_not_provided && (
                          <Col className="mb-2">
                            <Form.Group>
                              <Form.Label>PUC Expiry Date (Within 180 days)</Form.Label>
                              <div className="custom-calendar">
                                <DatePicker
                                  selected={this.state.puc_expiry_date ? new Date(this.state.puc_expiry_date) : null}
                                  onChange={this.handlePUCExpiryChange}
                                  className="myDatePicker"
                                  dateFormat="dd/MM/yyyy"
                                  placeholderText="DD/MM/YYYY"
                                  minDate={new Date()}
                                  maxDate={new Date(Date.now() + 180 * 24 * 60 * 60 * 1000)} // 180 days from now
                                />
                              </div>
                              <Form.Text className="text-info">
                                PUC expiry should be within 180 days from today
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        )}
                      </Row> */}

                      {/* Financier Details (Excel Requirement #11) */}
                      <Row className="mt-3">
                        <Col lg={12}>
                          <h6 className="text-primary">Financier Details</h6>
                        </Col>

                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Group>
                            <Form.Label>Financier Name</Form.Label>
                            <Form.Select
                              name="financier_name"
                              value={this.state.financier_name}
                              onChange={this.handleChange}
                            >
                              <option value="">Select Financier</option>
                              <option value="HDFC Bank">HDFC Bank</option>
                              <option value="ICICI Bank">ICICI Bank</option>
                              <option value="State Bank of India">State Bank of India</option>
                              <option value="Axis Bank">Axis Bank</option>
                              <option value="Kotak Mahindra Bank">Kotak Mahindra Bank</option>
                              <option value="Bajaj Finance">Bajaj Finance</option>
                              <option value="Mahindra Finance">Mahindra Finance</option>
                              <option value="Other">Other</option>
                            </Form.Select>
                            <Form.Text className="text-info">
                              Select financier
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        <Col md="3" sm="6" lg="4" className="mb-2">
                          <Form.Group>
                            <Form.Label>Financier Branch</Form.Label>
                            <Form.Control
                              type="text"
                              name="financier_branch"
                              value={this.state.financier_branch}
                              onChange={this.handleChange}
                              placeholder="Enter branch name"
                            />
                            <Form.Text className="text-info">
                              Enter financier branch details
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* T+1 Disclaimer for New Business/Breaking */}
                      {(this.state.business_type === "New Business" || this.state.business_type === "Breaking") && (
                        <Alert variant="warning" className="mt-3">
                          <Form.Check
                            type="checkbox"
                            label="I understand that for SATP New Business/Breaking policies, the policy start date will be T+1 (Tomorrow) "
                            checked={this.state.disclaimer_accepted}
                            onChange={this.handleDisclaimerAcceptance}
                            required
                          />
                        </Alert>
                      )}

                      <Row className="justify-content-between mt-3">
                        <Col lg="12" md="12">
                          <Button
                            className="rounded-0"
                            onClick={this.handlePreviousStep}
                            style={{ float: "left" }}
                            disabled={isLoading}
                          >
                            Back
                          </Button>
                          <Button
                            type="button"
                            className="rounded-0"
                            onClick={this.MototquoterequestsubmitHandler}
                            style={{ float: "right" }}
                            disabled={isLoading}
                          >
                            {isLoading ? "Submitting..." : "Get Enhanced SATP Quote"}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              )}
            </Accordion>
          </Col>
        </Row>

        <Row>
          <Col>
            <Alert variant="info" className="rounded-0 mt-3">
              <strong>Enhanced SATP Policy Information :</strong><br />
              • Precise Age Limits: Package (15Y 8M 25D), SATP (20Y 8M 23D), SAOD (15Y 8M 25D)<br />
              • NCB Auto-increment: 0→20%, 20→25%, 25→35%, 35→45%, 45→50%, 50→50%<br />
              • TATA AIG Restriction: Previous insurer cannot be TATA AIG<br />
              • Accessories Limit: ₹15,000 maximum for electrical/non-electrical<br />
              • Zero Dep + RTI Logic: Previous policy must have Zero Dep when both selected<br />
              • PUC Validation: Within 180 days or declaration available<br />
              • CPA Mandatory: Required for SAOD policies<br />
              • Terms & Conditions: Mandatory acceptance<br />
              • Breaking Period Logic: 90 days / 365 days categorization<br />
              • Financier Details: Required for loan cases
            </Alert>
          </Col>
        </Row>
      </div>
    );
  }
}